// dataseries.org — front-end (vanilla JS) over the same-origin plumber2 + DuckDB API.
// Design ported from the Claude Design handoff: landing hero -> search -> results
// -> detail with a hierarchical "Breakdown" selector, the original grey-first
// series palette, and colour = selector linkage.

import "./app.css";
import "uplot/dist/uPlot.min.css";
import * as XLSX from "xlsx";
import { rSnippet, pythonSnippet } from "./lib/snippets";
import { esc, fmtDate, fmtStat, parseStampUTC, periodLabel, relTime } from "./lib/format";
import { t, tn, initLocale, setLocale, getLocale, LOCALES, type Locale } from "./lib/locale";
import { dimsQuery, csvUrl, fetchCatalog, fetchCategories, fetchMaps, fetchMeta, fetchSeries } from "./lib/api";
import { buildChartModel, levelLabel as levelLabelOf, SERIES_COLORS } from "./lib/chartmodel";
import { freqLabel, periodMidShiftSec, defaultTransform,
  transformOptions as transformOptionsPure, transformUnit as transformUnitPure } from "./lib/transforms";
import { label, badgeOf, sourceName, splitDimOf, singleSelectDims, lineDims,
  defaultSelection, selectionIsDefault as selectionIsDefaultPure, pinnedDefault, firstRowSelection, levelPossible as levelPossiblePure, srcOf,
  seriesTitle, unitOf, changeDiff } from "./lib/meta";
import { parseRoute, buildDimsParam, parseExtraKey, buildShareHash, placeFromUrl, placeUrl, mapsTabPath, type BrowseTab } from "./lib/route";
import { topicTree, topicCount, browseIntro, mapCard, mapRowMeta, findMap, MAPS_LEAF, POPULATION_MAP, type MapCard } from "./lib/browse";
import { toCsv, CSV_BOM } from "./lib/csv";
import { toggleLevel, pickExclusive, viewFromRoute, MAX_EXTRAS } from "./lib/selection";
import { searchGrouped as searchGroupedPure, mapForSeries, type CatalogItem } from "./lib/search";
import { initEmbedTheme, initSiteTheme, notifyEmbedReady, parseThemePref, pinnedTheme, setThemePref, themePref } from "./lib/theme";
import { watchEmbedHeight } from "./lib/embed-height";
import { createNavigation } from "./lib/navigation";
import { zoomedRaw } from "./lib/xwindow";
import { chartScale, chartShift, clearChart, drawLines, hasChart, onChartEvents, showRange } from "./chart";
import { initialState, openedState, type AppState } from "./lib/state";

const API = "";

// "Selected" fill for a breakdown toggle that carries no line colour (the dash
// dim, or any dim in the 3+-varying fallback) — a neutral slate, not a hue that
// would read as a specific line's colour.
const NEUTRAL_ON = "#7c8696";
// Soft cap: warn (don't refuse) once the cross-product exceeds this many lines.
const LINE_WARN = 16;
// Source badge colours (KOF blue, SECO green, FSO petrol, SNB brown) live in
// app.css as `.badge.src-*` tokens, so they have a dark variant — see badgeHTML.
// Landing chips + featured rows are data-driven from the catalog (no hardcoded ids).
// The "Try" chips fill the landing with the whole catalog: featured datasets first
// (short labels from their `featured` flag), then the rest by title. Always a string
// label (never an object), so no "[object Object]".
// `featured` is a short chip label string when set; non-featured datasets come
// through as an empty object {} from the serializer, so coerce to a real label
// (empty -> not featured) to avoid "[object Object]".
const featLabel = c => (typeof c.featured === "string" ? c.featured : label(c.featured)) || "";  // string (1.0) | i18n object (1.1)
// Landing "Try" chips: two featured datasets + GDP (the headline series), GDP last.
const examples = () => {
  const feat = state.catalog.filter(c => featLabel(c));
  const gdp = feat.find(c => c.id === "ch_seco_gdp");
  const base = feat.filter(c => c.id !== "ch_seco_gdp").slice(0, 2);
  return [...base, gdp].filter(Boolean).map(c => ({ label: featLabel(c), id: c.id }));
};
// Six, so the Maps block that follows them shows without scrolling.
const mainDatasets = () => state.catalog.filter(c => featLabel(c)).slice(0, 6).map(c => c.id);
const ICON = {
  search: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.3-4.3"/></svg>',
  chev:   '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>',
  back:   '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5M11 6l-6 6 6 6"/></svg>',
  arrow:  '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>',
  chartSm: '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17l5-6 4 3 8-9"/></svg>',
  mapSm:  '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M9 4 3 6.5V20l6-2.5 6 2.5 6-2.5V4l-6 2.5z"/><path d="M9 4v13.5M15 6.5V20"/></svg>',
  map:    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M9 4 3 6.5V20l6-2.5 6 2.5 6-2.5V4l-6 2.5z"/><path d="M9 4v13.5M15 6.5V20"/></svg>',
  link:   '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5"/><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5"/></svg>',
  github: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 .5a12 12 0 0 0-3.8 23.4c.6.1.8-.3.8-.6v-2c-3.3.7-4-1.6-4-1.6-.6-1.4-1.3-1.8-1.3-1.8-1.1-.7.1-.7.1-.7 1.2 0 1.8 1.2 1.8 1.2 1.1 1.8 2.8 1.3 3.5 1 .1-.8.4-1.3.8-1.6-2.7-.3-5.5-1.3-5.5-5.9 0-1.3.5-2.4 1.2-3.2 0-.4-.5-1.6.2-3.2 0 0 1-.3 3.3 1.2a11.5 11.5 0 0 1 6 0C17.3 5 18.3 5.3 18.3 5.3c.7 1.6.2 2.8.1 3.2.8.8 1.2 1.9 1.2 3.2 0 4.6-2.8 5.6-5.5 5.9.4.4.8 1.1.8 2.2v3.3c0 .3.2.7.8.6A12 12 0 0 0 12 .5Z"/></svg>'
};
// rail-card marker icons (matching the design's RailCard)
const RAIL_ICONS = {
  compare:  ["M4 19V5", "M4 19h16", "M8 15l3-4 2.5 2L18 7"],
  details:  ["M7 3h8l4 4v14H7z", "M15 3v4h4", "M9.5 13h5", "M9.5 16.5h5"],
  code:     ["M9 8l-4 4 4 4", "M15 8l4 4-4 4"],
  download: ["M12 3v11", "M8 10l4 4 4-4", "M5 20h14"]
};
const CARET = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>';
const CHECK = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L20 7"/></svg>';
const PLUS = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>';
const CLOSE = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
const RESET = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg>';
const dimMark = (on, col) => `<span class="dim-mark"${on ? ` style="background:${col};border-color:${col}"` : ""}>${on ? CHECK : ""}</span>`;
// Trailing right-edge toggle for breakdown rows (SEARCH_SPEC §6): + when off, fills
// with the series' line colour + check when on — same affordance as the search +.
const dimToggle = (on, col) => `<span class="dim-toggle ${on ? "on" : ""}"${on ? ` style="background:${col};border-color:${col}"` : ""}>${on ? CHECK : PLUS}</span>`;
// Legend/marker swatch as a short stroked line, so the dash pattern (the 2nd
// varying line dim's channel) reads in the legend itself, not only on the chart.
const legSwatch = (color, dash) =>
  `<svg class="leg-swatch" width="20" height="10" viewBox="0 0 20 10" aria-hidden="true"><line x1="0" y1="5" x2="20" y2="5" stroke="${color}" stroke-width="2.8"${dash && dash.length ? ` stroke-dasharray="${dash.join(",")}"` : ""}/></svg>`;
const svgIcon = (paths, size = 15, sw = 1.9) =>
  `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round">${paths.map(p => `<path d="${p}"/>`).join("")}</svg>`;

// The app's single mutable state, typed in lib/state. Listener handles and other
// wiring live with the functions that own them, not here.
const state: AppState = initialState();
const metaCache = {};   // datasetId -> meta (filled on demand by ensureMeta/openDataset)
// Every navigation takes a ticket here, and async work checks its own after each
// await (lib/navigation), so a slow response can't pull the page back to where
// the visitor was.
const nav = createNavigation();
// DOM query helpers, typed as HTMLElement(s) so .dataset/.onclick/.value resolve.
// They may return null at runtime; under the lenient baseline we don't enforce the
// null check at every call site.
const $ = (sel: string) => document.querySelector(sel) as any; // loose: many are inputs (.value)
const $$ = (sel: string, root: ParentNode = document) =>
  Array.from(root.querySelectorAll(sel)) as HTMLElement[];
const app = () => document.getElementById("app") as HTMLElement;
// Document-level listeners that a view adds again on every render. Each slot
// holds one, so re-wiring replaces the previous listener instead of stacking.
const docSlots = new Map<string, [string, EventListener]>();
function docListener(slot: string, type: string, fn: (e: any) => void) {
  const old = docSlots.get(slot);
  if (old) document.removeEventListener(old[0], old[1]);
  docSlots.set(slot, [type, fn]);
  document.addEventListener(type, fn);
}

async function jget(path) {
  const r = await fetch(API + path);
  if (!r.ok) throw new Error(path + " -> " + r.status);
  return r.json();
}
// esc imported from ./lib/format; label/badgeOf/sourceName + the dataset model
// (splitDimOf, lineDims, defaultSelection, …) from ./lib/meta.
// Colour comes from a class (see .badge.src-* in app.css), not an inline style,
// so badges follow a light/dark flip without re-rendering the view.
// The source badge says who the data comes from, from the catalog's source key
// ("fso-focbs" -> FSO, "eurostat" -> EUROSTAT); the id prefix only says where
// we fetched it (ch_fso_hicp is Eurostat's HICP).
function badgeFor(id) {
  const src = (state.catalog.find(c => c.id === id) || {}).source;
  const key = src && typeof src === "object" ? (src as { key?: string }).key : "";
  return key ? String(key).split("-")[0].toUpperCase() : badgeOf(id);
}
function badgeHTML(id) {
  return badgeCodeHTML(badgeFor(id));
}
function badgeCodeHTML(code) {
  return `<span class="badge src-${esc(code.toLowerCase())}">${esc(code)}</span>`;
}

// ---- maps.dataseries.org ------------------------------------------------------
// GET /maps lists its maps. A map without `series` is a map-only row: the whole
// row links to the map, marked by the MAP badge in the browse view and by one
// small map icon in the search menu. A map with `series` shows the same
// statistic as those datasets; their rows get a Map pill in the browse view, and
// the series page links to it ("Show on map"). The browse view's Maps tab lists
// every map as a picture card (lib/browse).
// Links carry the UI language: the maps site would otherwise open in German.
const mapHref = (url: string) => `${url}&lang=${getLocale()}`;
const mapMarkHTML = (levels: string) =>
  `<span class="meta-map" title="${esc(t("Map"))}${levels ? ": " + esc(levels) : ""}" aria-label="${esc(t("Map"))}">${ICON.mapSm}</span>`;
function mapPillHTML() {
  const m = state.current && !state.embed && mapForSeries(state.maps, state.current.id);
  if (!m) return "";
  return `<a class="map-pill" id="map-pill" href="${esc(mapHref(m.url))}" title="${esc(label(m.levels_label))}">${ICON.map}<span>${esc(t("Show on map"))}</span></a>`;
}

// ---- shell ------------------------------------------------------------------
function render() {
  app().innerHTML = headerHTML() + `<div id="view" class="grow"></div>` + footerHTML();
  wireHeader();
  renderView();
}
function headerHTML() {
  return `<header class="hdr"><div class="wrap hdr-row">
    <a class="wordmark" id="home" href="${getLocale() === "en" ? "/" : `/${getLocale()}/`}">
      <span><b>data</b><span class="wm-sub">series.org</span></span>
    </a>
    <nav class="nav">
      <!-- A real link, like the two below: /datasets is a server-rendered
           document now, so this is shareable, crawlable and works without JS.
           The click handler still opens the in-app browse view for anyone
           running JS, so the interaction is unchanged. -->
      <a class="nav-link" id="nav-browse" href="${datasetsPath()}">${esc(t("Datasets"))}</a>
      <!-- The browse view on its Maps tab. /datasets/maps is the SPA shell with
           the maps listed server-side, so this too works without JS. -->
      <a class="nav-link" id="nav-maps" href="${mapsTabPath(getLocale())}">${esc(t("Maps"))}</a>
      <!-- Real links, not [data-go] view state: these are server-rendered
           pages now, one per language. -->
      <a class="nav-link" href="${pagePath("about")}">${esc(t("About"))}</a>
      <a class="nav-link" href="${pagePath("libraries")}">${esc(t("Libraries"))}</a>
      <a class="nav-link nav-gh" href="https://github.com/cynkra/dataseries-data" target="_blank" rel="noopener" title="Open pipeline on GitHub">${ICON.github}</a>
    </nav>
  </div></header>`;
}
// Footer freshness line: the catalog's most-recent `fetched` is the last
// successful scrape. max() (not min()) so a single skipped source doesn't drag
// the date — only a whole missed run slips it a day (per-source staleness is the
// health board's job). Green dot when fresh; amber once older than a daily cycle.
const FRESH_STALE_HOURS = 36;
function lastFetched(): Date | null {
  let best: Date | null = null;
  for (const c of (state.catalog || [])) {
    const d = parseStampUTC(c.fetched);
    if (d && (!best || d > best)) best = d;
  }
  return best;
}
function footerFreshHTML() {
  const d = lastFetched();
  if (!d) return "";
  const stale = (Date.now() - d.getTime()) / 3.6e6 > FRESH_STALE_HOURS;
  const exact = d.toISOString().replace("T", " ").slice(0, 19) + " UTC";
  return `<div class="foot-fresh${stale ? " stale" : ""}" title="${esc(t("Last successful scrape:"))} ${esc(exact)}">
        <span class="foot-fresh-dot"></span>${esc(t("Data updated"))} <strong>${esc(relTime(d))}</strong> · ${esc(tn("{n} datasets", state.catalog.length))}</div>`;
}
function footerHTML() {
  return `<footer class="foot"><div class="wrap">
    <div class="foot-top">
      <div class="wordmark"><span><b>data</b><span class="wm-sub">series.org</span></span></div>
      <p class="foot-open">${esc(t("Open Swiss economic data, in one place. Sourced from the national statistical providers."))}</p>
    </div>
    <div class="foot-bar">
      ${footerFreshHTML()}
      ${langControlHTML()}
      ${themeControlHTML()}
      <div class="foot-dev"><span>${esc(t("Developed by"))}</span><a href="https://cynkra.com" target="_blank" rel="noopener"><img class="foot-cynkra" src="/assets/logo-cynkra.png" alt="cynkra"></a></div>
    </div>
  </div></footer>`;
}
// Server-rendered content pages (api/pages.R), reached by real links. They
// exist in all four languages, at the same prefix as the rest of the site.
const pagePath = (slug: string): string => {
  const loc = getLocale();
  return loc === "en" ? `/${slug}` : `/${loc}/${slug}`;
};
const datasetsPath = (): string => pagePath("datasets");

// Language: EN | DE | FR | IT. Switching stores the preference and NAVIGATES to
// that language's URL (see localeUrl). A full page load is also what re-resolves
// every baked string — chart options, the sub-index, tooltips — instead of
// chasing each cache. Rare action, robust semantics.
// The same page in another language, as a URL. Standard practice for a language
// selector is to NAVIGATE to the other language's address rather than re-render
// in place: the URL stays the source of truth, so the page is shareable and
// bookmarkable in the language you are reading, Back works, and the server
// renders that language directly.
//
// It is also required for correctness here. The switcher used to setLocale() +
// reload(); on a /de/ page that reloaded the SAME url, and the path prefix
// outranks the stored preference, so switching away from German silently did
// nothing. Only /d/ has localized server routes, so other paths keep their
// address and fall back to the stored preference.
function localeUrl(loc: Locale): string {
  const bare = location.pathname.replace(/^\/(de|fr|it)(?=\/|$)/, "") || "/";
  // Every page with a server route has a real localized URL, so the switcher
  // navigates to it rather than tacking on ?lang=. That is now the whole site:
  // the dataset pages, the homepage, and the three static pages.
  const localizable = bare.startsWith("/d/") || bare === "/" || /^\/datasets\/maps\/?$/.test(bare);
  const path = loc !== "en" && localizable
    ? (bare === "/" ? `/${loc}/` : `/${loc}${bare}`)
    : bare;
  return path + location.search + location.hash;
}

function langControlHTML() {
  const cur = getLocale();
  return `<div class="theme-ctl"><span id="lang-lbl">${esc(t("Language"))}</span>
      <div class="theme-seg" role="group" aria-labelledby="lang-lbl">
        ${LOCALES.map(l =>
          `<button class="theme-btn ${cur === l ? "on" : ""}" data-lang="${l}" aria-pressed="${cur === l}">${l.toUpperCase()}</button>`).join("")}
      </div></div>`;
}
// Appearance: Auto (follow the OS — the default nobody has to touch) | Light | Dark.
function themeControlHTML() {
  const p = themePref();
  return `<div class="theme-ctl"><span id="theme-lbl">${esc(t("Appearance"))}</span>
      <div class="theme-seg" role="group" aria-labelledby="theme-lbl">
        ${[["auto",t("Auto")],["light",t("Light")],["dark",t("Dark")]].map(([v,l]) =>
          `<button class="theme-btn ${p===v?"on":""}" data-theme-pref="${v}" aria-pressed="${p===v}">${l}</button>`).join("")}
      </div></div>`;
}
let navWired = false;   // the delegated handlers below go on #app once
function wireHeader() {
  // A real link (opens in a new tab with a modifier); a plain click stays in the app.
  $("#home").onclick = (e: MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault(); goView("landing", { query: "" });
  };
  // Now an <a href="/datasets">, so the default action must be suppressed for
  // JS visitors — otherwise clicking the nav leaves the app for the document.
  // Modified clicks (new tab, middle-click) fall through to the real link.
  $("#nav-browse").onclick = (e: MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    goView("results", { query: "", tab: "datasets" });
  };
  $("#nav-maps").onclick = (e: MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    goView("results", { query: "", tab: "maps" });
  };
  // one delegated handler for every internal [data-go] link (nav, footer, page bodies)
  if (!navWired) {
    navWired = true;
    app().addEventListener("click", e => {
      const t = (e.target as HTMLElement).closest("[data-go]") as HTMLElement;
      if (!t) return;
      e.preventDefault();
      goView(t.dataset.go);
    });
    // Language control. Delegated; stores the preference and reloads (the view
    // survives via the URL). See langControlHTML for why reload, not re-render.
    app().addEventListener("click", e => {
      const el = (e.target as HTMLElement).closest("[data-lang]") as HTMLElement;
      if (!el) return;
      const l = el.dataset.lang as Locale;
      setLocale(l);                    // remember the choice for other pages
      location.assign(localeUrl(l));   // …and go to that language's URL
    });
    // Appearance control. Delegated for the same reason, and it only restyles —
    // the view is not re-rendered, so nothing is refetched and no state is lost.
    app().addEventListener("click", e => {
      const t = (e.target as HTMLElement).closest("[data-theme-pref]") as HTMLElement;
      if (!t) return;
      setThemePref(parseThemePref(t.dataset.themePref));
      $$("[data-theme-pref]").forEach(b => {
        const on = b.dataset.themePref === themePref();
        b.classList.toggle("on", on);
        b.setAttribute("aria-pressed", String(on));
      });
    });
  }
}
// Navigate to a non-detail view; clears the deep-link hash so stale chart URLs
// don't linger when you move to a static/list page.
// Go to a non-detail view (landing, results). It gets its own history entry and
// its own address (lib/route placeUrl), so Back returns to the page before and a
// reload or language switch stays here instead of reopening the last dataset.
function goView(view, extra?) {
  nav.go();   // a dataset still loading must not pull the view back to itself
  if (extra) Object.assign(state, extra);
  state.view = view;
  writeUrl(placeUrl({ view, query: state.query || "", tab: state.tab }, getLocale()), true);
  renderView();
}
// Point the address bar at the current view. `push` adds a history entry (the
// visitor went somewhere); otherwise the current entry is updated in place
// (typing a query, changing a line). history.state carries the view, which is
// how Back tells the browse view apart from the landing: both live at "/".
function writeUrl(url: string, push: boolean) {
  const hs = { view: state.view, query: state.view === "results" ? state.query || "" : "", tab: state.tab };
  const cur = history.state as { view?: string } | null;
  const moved = url !== location.pathname + location.search + location.hash || !cur || cur.view !== hs.view;
  if (push && moved) history.pushState(hs, "", url);
  else history.replaceState(hs, "", url);
}
// ?q=<text> opens the results for that text: maps.dataseries.org's menu hands
// a typed query over this way, and a results page can be bookmarked or shared.
// The results page keeps it current while you type.
function syncQueryParam(q: string) {
  writeUrl(placeUrl({ view: "results", query: q, tab: state.tab }, getLocale()), false);
}

// Static content pages (/about, /libraries, /licenses) are NOT rendered here.
// They were view state with no URL, so they could not be linked, shared, put in
// the sitemap, or read by any crawler that does not run JS — which is most of
// the LLM ones. They are server-rendered documents now; api/pages.R holds the
// text, and the nav links to them with a plain href. Do not reintroduce a copy
// here: two versions of the licence text is exactly the drift that motivated
// moving them.

// The series page names its chart in the tab; the other views get back the
// title the page was served with (only known when it was not a series page).
const homeTitle = /\/d\//.test(location.pathname) ? "dataseries.org" : document.title;
function renderView() {
  const v = $("#view");
  if (state.view !== "detail") document.title = homeTitle;
  // The series page runs at --maxw-wide; every other view is a text column at
  // --maxw. The header spans whichever the current view uses, so the wordmark
  // stays flush with the content under it instead of floating in from the edge.
  document.body.classList.toggle("wide", state.view === "detail");
  if (state.view === "landing") { v.innerHTML = landingHTML(); wireLanding(); }
  else if (state.view === "loading") { v.innerHTML = `<div class="empty">${t("Loading…")}</div>`; }
  else if (state.view === "results") { v.innerHTML = resultsHTML(); wireResults(); }
  else if (state.view === "detail") { renderDetail(v); }
  syncNav();
  window.scrollTo({ top: 0 });
}
// The header link for the browse tab on screen reads as the current place.
function syncNav() {
  const on = state.view === "results" && !state.query.trim() ? state.tab : null;
  $("#nav-browse")?.classList.toggle("on", on === "datasets");
  $("#nav-maps")?.classList.toggle("on", on === "maps" && state.maps.length > 0);
}

// ---- landing ----------------------------------------------------------------
// The hero headline carries a hand-placed line break, tuned to where the English
// sentence reads best. That break point is language-specific, so the catalog
// string owns it: a newline in the translation becomes the <br>. Escaped either
// side of the split, so the catalog never injects markup.
function heroHeadline(): string {
  return t("Switzerland's economic\ndata, in one place.").split("\n").map(esc).join("<br>");
}

function landingHTML() {
  return `<section class="hero">
    <div class="hero-media" role="img" aria-label="A row of white sneakers with one red Converse standing out"><div class="hero-scrim"></div></div>
    <div class="wrap hero-inner"><div class="hero-copy fade-up">
      <h1 class="hero-h1">${heroHeadline()}</h1>
      <div class="hero-search" id="hero-search"><form class="search search-big" id="hero-form">
        <span class="search-ic">${ICON.search}</span>
        <input id="hero-q" placeholder="${t('Search…')}" spellcheck="false" autocomplete="off">
        <button type="button" class="search-clear hidden" id="hero-clear" aria-label="Clear">${CLOSE}</button>
        <button type="submit" class="search-go search-go-minimal" aria-label="Search">${ICON.arrow}</button>
      </form><div class="switcher-list hero-drop hidden" id="hero-drop"></div></div>
      <div class="hero-eg"><span>${esc(t("Try"))}</span><div class="hero-eg-chips">
        ${examples().map(e => `<button class="chip chip-eg" data-id="${e.id}">${esc(e.label)}</button>`).join("")}${mapChipHTML()}
      </div></div>
    </div></div>
  </section>`;
}
// The fourth "Try" chip: the population map, a link out like every map link.
// Absent until GET /maps lands (onMapsLoaded adds it then), and absent for good
// when the list does not have that map.
function mapChipHTML() {
  const m = findMap(state.maps || [], POPULATION_MAP);
  return m ? `<a class="chip chip-eg chip-map" href="${esc(mapHref(m.url))}">${ICON.mapSm}<span>${esc(t("Population map"))}</span></a>` : "";
}
function wireLanding() {
  const inp = $("#hero-q"), drop = $("#hero-drop"), wrap = $("#hero-search");
  const renderDrop = () => {
    const q = inp.value.trim();
    const rows = searchGrouped(q, null);
    drop.classList.remove("hidden");
    drop.innerHTML = `<div class="switcher-hint">${q ? t("Results") : t("Popular series")}<span class="switcher-hint-key">${t("click to open")}</span></div>` +
      (rows.length ? dropRowsHTML(q ? rows.slice(0, 50) : rows, false) : `<div class="switcher-empty">No series match “${esc(q)}”</div>`);
    $$("button.switcher-open", drop).forEach(el => el.onclick = () =>
      el.dataset.subcode != null ? openDataset(el.dataset.id, { dim: el.dataset.subdim, code: el.dataset.subcode }) : openDataset(el.dataset.id));
  };
  const clear = $("#hero-clear");
  const sync = () => { renderDrop(); if (clear) clear.classList.toggle("hidden", !inp.value); };
  // Open the dropdown only when there is something typed. Bare focus on the
  // empty box (incl. the autofocus below, and Safari re-focus quirks) stays
  // closed — the dropdown opens on input, or reopens on focus if a query
  // remains from earlier typing.
  inp.onfocus = () => { if (inp.value.trim()) renderDrop(); };
  inp.oninput = sync;
  inp.onkeydown = dropdownKeydown(inp, drop, renderDrop);
  if (clear) clear.onclick = () => { inp.value = ""; clear.classList.add("hidden"); inp.focus(); renderDrop(); };
  // Enter still routes to the full browse-all results page (dropdown is primary).
  $("#hero-form").onsubmit = e => { e.preventDefault(); goView("results", { query: inp.value.trim(), tab: "datasets" }); };
  // close on outside click / Escape
  docListener("hero-out", "mousedown", e => { if (wrap && !wrap.contains(e.target)) drop.classList.add("hidden"); });
  docListener("hero-esc", "keydown", e => { if (e.key === "Escape") drop.classList.add("hidden"); });
  $$("button.chip-eg").forEach(el => el.onclick = () => openDataset(el.dataset.id));
  // Autofocus so you can type / arrow-key right away, dropdown still closed
  // (it only opens once there is a query — see onfocus/oninput above).
  // Only on fine-pointer (non-touch) devices, so phones don't pop the keyboard.
  if (window.matchMedia && window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
    inp.focus({ preventScroll: true });
  }
}

// ---- concept tree (browse view when there's no query) -----------------------
// Fallback order if GET /categories is unavailable; the live order comes from the
// catalog's categories endpoint (data/categories.json), the single source of truth.
const CONCEPT_ORDER = ["National accounts", "Prices", "Labour",
  "Business cycle and sentiment", "Interest rates and yields", "Exchange rates",
  "Financial markets", "External sector", "Money and banking", "Domestic economy",
  "Population and demographics", "Payment systems"];
// Group identity is the category KEY, never the display name — translating the
// names must not change which datasets land in which group (it did: 0/12 matched
// in German). Falls back to the name for pre-1.1 data that has no key.
const categoryOrder = () => (state.categories && state.categories.length)
  ? state.categories.map((c: any) => c.key || label(c.name)) : CONCEPT_ORDER;
const catName = (k: string) => {
  const c = (state.categories || []).find((x: any) => (x.key || label(x.name)) === k);
  if (c) return label(c.name);
  // a topic only the maps use ("transport") carries its name on the map entry
  const m = (state.maps || []).find((x: any) => x.topic && x.topic.key === k);
  return m ? label(m.topic.name) : k;
};
// The grouping itself (topics, leaves, where the maps go, the counts) is in
// lib/browse; groupKey and splitConcept live there too.

// A dataset row opens its chart. One whose statistic also has a map gets the
// Map pill, which opens the map. The pill sits beside the button, not in it (a
// link inside a button is invalid), placed over the row by CSS; the button
// keeps a slot free for it.
function datasetRowHTML(d) {
  const m = mapForSeries(state.maps, d.id);
  const row = `<button class="row" data-id="${d.id}">
    <div class="row-badge">${badgeHTML(d.id)}</div>
    <div class="row-main">
      <div class="row-title">${esc(label(d.title))}</div>
      <div class="row-meta"><span>${esc(srcOf(d.source))}</span><span class="row-dot">·</span><span>${esc(freqLabel(d.frequency))}</span><span class="row-dot">·</span><span class="tnum">${esc((d.start||"").slice(0,4))}–${esc((d.end||"").slice(0,4))}</span></div>
    </div>
    ${m ? `<span class="row-pill-slot"></span>` : ""}
    <div class="row-chev">${ICON.chev}</div>
  </button>`;
  if (!m) return row;
  return `<div class="row-wrap">${row}<a class="map-pill row-map-pill" href="${esc(mapHref(m.url))}" title="${esc(label(m.levels_label))}">${ICON.mapSm}<span>${esc(t("Map"))}</span></a></div>`;
}
// A map-only entry on /datasets and in the results: a link out to the map. The
// MAP badge takes the source badge's place, the source moves to the meta line.
function mapRowHTML(m) {
  const meta = mapRowMeta(m).map((x, i, a) =>
    `<span${i === a.length - 1 ? ` class="tnum"` : ""}>${esc(x)}</span>`).join(`<span class="row-dot">·</span>`);
  return `<a class="row row-maponly" href="${esc(mapHref(m.url))}">
    <div class="row-badge"><span class="badge badge-map">${ICON.mapSm}${esc(t("Map"))}</span></div>
    <div class="row-main">
      <div class="row-title">${esc(label(m.title))}</div>
      <div class="row-meta">${meta}</div>
    </div>
    <div class="row-ext">maps.dataseries.org ↗</div>
  </a>`;
}
function conceptTreeHTML() {
  return `<div class="ctree">` + topicTree(state.catalog, state.maps || [], categoryOrder()).map(g => {
    const open = !state.closedGroups.has(g.key);
    const body = g.leaves.map(({ leaf, rows }) => {
      const html = rows.map(r => r.kind === "map" ? mapRowHTML(r.item) : datasetRowHTML(r.item)).join("");
      const head = leaf === MAPS_LEAF ? t("Maps") : leaf;
      return head ? `<div class="leaf"><div class="leaf-h">${esc(head)}</div>${html}</div>` : html;
    }).join("");
    return `<section class="cgroup${open ? " open" : ""}">
      <button class="cgroup-h" data-grp="${esc(g.key)}">
        <span class="cgroup-chev">${ICON.chev}</span>
        <span class="cgroup-name">${esc(catName(g.key))}</span><span class="cgroup-n">${esc(topicCount(g.datasets, g.maps))}</span>
      </button>
      <div class="cgroup-body">${body}</div>
    </section>`;
  }).join("") + `</div>`;
}

// ---- the Maps tab ---------------------------------------------------------
// One picture card per map, all of them, also those with `series`. The card
// opens the map; a map with series adds a line that opens the same statistic as
// a chart. Without a picture (or while it does not load) the card shows a
// neutral placeholder with the map icon, never a broken image.
function mapCardHTML(c: MapCard) {
  const pic = c.thumbnail ? `<img class="mcard-img" src="${esc(c.thumbnail)}" alt="" decoding="async">` : "";
  const also = c.chart
    ? `<a class="mcard-also" href="${esc(datasetPath(c.chart.id))}" data-id="${esc(c.chart.id)}">${ICON.chartSm}<span>${esc(t("Also as a chart:"))} ${esc(c.chart.title)}</span></a>` : "";
  return `<div class="mcard" data-map="${esc(c.id)}">
    <a class="mcard-open" href="${esc(mapHref(c.url))}">
      <div class="mcard-th"><span class="mcard-ph">${ICON.map}</span>${pic}${c.levels ? `<span class="mcard-lvl">${esc(c.levels)}</span>` : ""}</div>
      <div class="mcard-bd"><div class="mcard-t">${esc(c.title)}</div><div class="mcard-m tnum">${esc(c.meta)}</div></div>
    </a>${also}
  </div>`;
}
const mapGridHTML = () =>
  `<div class="mgrid">${(state.maps || []).map(m => mapCardHTML(mapCard(m, state.catalog))).join("")}</div>`;
// A dataset's page, in the UI language (the same shape sharePath() writes).
const datasetPath = (id: string) => (getLocale() === "en" ? "" : `/${getLocale()}`) + `/d/${id}`;

// The two tabs. Shown only once the maps list is there: without maps the view
// is the topic list it always was.
function browseTabsHTML() {
  const nMaps = state.maps.length;
  const tab = (key: BrowseTab, name: string, n: number) => {
    const on = state.tab === key;
    return `<button class="seg-btn browse-tab${on ? " on" : ""}" role="tab" aria-selected="${on}" data-tab="${key}">${esc(name)}<em>${n}</em></button>`;
  };
  return `<div class="seg browse-tabs" role="tablist" aria-label="${esc(t("Datasets and maps"))}">${tab("datasets", t("Datasets"), state.catalog.length)}${tab("maps", t("Maps"), nMaps)}</div>`;
}
function resultsBodyHTML() {
  if (!state.query) {
    const nMaps = (state.maps || []).length;
    const intro = `<div class="results-count"><span>${esc(browseIntro(state.catalog.length, nMaps))}</span></div>`;
    if (!nMaps) return intro + conceptTreeHTML();
    return intro + browseTabsHTML() + (state.tab === "maps" ? mapGridHTML() : conceptTreeHTML());
  }
  const q = state.query.trim();
  // Same grouping as the top search bar: each matching dataset on its own row,
  // with its matching sub-series indented directly beneath it (not a flat list
  // of series each tagged "part of …").
  const rows = searchGrouped(state.query, null);
  if (!rows.length)
    return `<div class="results-count"><span><b>0</b> results for “${esc(q)}”</span></div><div class="empty">No series match “${esc(q)}”. Try GDP, inflation, tourism or unemployment.</div>`;
  const rowsHTML = rows.map(r => {
    if (r.mapOnly) { const m = state.maps.find(x => x.id === r.id); return m ? mapRowHTML(m) : ""; }
    if (r.depth === 0) { const d = state.catalog.find(c => c.id === r.id); return d ? datasetRowHTML(d) : ""; }
    return subResultRowHTML(r);
  }).join("");
  const nDs = rows.filter(r => r.depth === 0).length;
  const nSub = rows.length - nDs;
  const count = `<b>${nDs}</b> dataset${nDs !== 1 ? "s" : ""}${nSub ? ` · <b>${nSub}</b> series` : ""} matching “${esc(q)}”`;
  return `<div class="results-count"><span>${count}</span></div><div class="rows">${rowsHTML}</div>`;
}
// A sub-series search hit, rendered as an indented child row under its dataset
// (mirrors the top-search dropdown's grouped look). Click opens the dataset with
// that series pre-selected — wireRows reads data-subdim/data-subcode.
function subResultRowHTML(r) {
  return `<button class="row row-sub" data-id="${r.id}" data-subdim="${esc(r.subdim)}" data-subcode="${esc(r.subcode)}">
      <div class="row-badge"><span class="row-subtick"></span></div>
      <div class="row-main">
        <div class="row-title">${esc(r.title)}</div>
        ${r.freq ? `<div class="row-meta"><span>${esc(r.freq)}</span></div>` : ""}
      </div>
      <div class="row-chev">${ICON.chev}</div>
    </button>`;
}
function resultsHTML() {
  return `<main class="wrap results-wrap">
    <div class="results-search"><form class="search" id="res-form">
      <span class="search-ic">${ICON.search}</span>
      <input id="res-q" placeholder="${t('Search series…')}" value="${esc(state.query)}" spellcheck="false" autocomplete="off">
    </form></div>
    <div id="results-body">${resultsBodyHTML()}</div>
  </main>`;
}
function wireResults() {
  const inp = $("#res-q");
  const redraw = () => { $("#results-body").innerHTML = resultsBodyHTML(); wireBody(); };
  syncQueryParam(state.query.trim());
  inp.oninput = () => { state.query = inp.value; syncQueryParam(state.query.trim()); redraw(); };
  $("#res-form").onsubmit = e => e.preventDefault();
  wireBody();
  // The catalog gives us the 53 dataset titles immediately; individual series
  // come from the sub-series index, whose preload is deferred to idle (so it
  // can't race a chart fetch). On this page there's no chart, and an engaged
  // user can starve requestIdleCallback by clicking/typing right away — which
  // left the search matching dataset titles only. Pull the index now and
  // re-render the open results when it lands so series always become searchable.
  if (!state.subReady) buildSubIndex().then(() => { if (state.view === "results") redraw(); });
  inp.focus();
}
function wireGroups() {
  $$(".cgroup-h").forEach(h => h.onclick = () => {
    const g = h.dataset.grp, sec = h.closest(".cgroup");
    if (state.closedGroups.has(g)) { state.closedGroups.delete(g); sec.classList.add("open"); }
    else { state.closedGroups.add(g); sec.classList.remove("open"); }
  });
}
// Everything clickable in #results-body, after each (re)render of it.
function wireBody() {
  wireRows(); wireGroups();
  $$(".browse-tab").forEach(b => b.onclick = () => {
    if (b.dataset.tab !== state.tab) goView("results", { query: "", tab: b.dataset.tab as BrowseTab });
  });
  // "Also as a chart": a real /d/ link, opened in the app unless the visitor
  // asks for a new tab.
  $$("a.mcard-also").forEach(a => a.onclick = (e: MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    openDataset(a.dataset.id);
  });
  // A picture that does not load leaves the placeholder under it showing.
  $$("img.mcard-img").forEach((img: any) => {
    if (img.complete && !img.naturalWidth) img.remove();
    else img.addEventListener("error", () => img.remove(), { once: true });
  });
}
function wireRows() {
  // Map-only rows are plain links (a.row), so only the buttons open a dataset.
  $$("button.row").forEach(el => el.onclick = () =>
    el.dataset.subcode != null
      ? openDataset(el.dataset.id, { dim: el.dataset.subdim, code: el.dataset.subcode })
      : openDataset(el.dataset.id));
}

// ---- dataset model ----------------------------------------------------------
// splitDimOf / singleSelectDims / lineDims / defaultSelection / selectionIsDefault
// / levelPossible are pure and live in ./lib/meta (imported above). The thin
// wrappers below bind the state-reading ones to the live selection/combos.
// Line dims the user has opened to >1 level right now — these actually vary, so
// they carry a visual channel. Selection-based (UI/colour-offset use); drawChart
// recomputes from the drawn tuples to ignore dims left constant by empty cells.
function varyingDims() {
  const m = state.current && state.current.meta;
  if (!m) return [];
  return lineDims(m).filter(d => (state.selection[d] || []).length > 1);
}
// True when the current selection already equals the factory default (so the
// Reset affordance can stay hidden until the user has actually changed something).
function selectionIsDefault() {
  if (!state.current) return true;
  return selectionIsDefaultPure(defaultSelection(state.current.meta), state.selection || {});
}
// Restore the Breakdown to the dataset's factory defaults (one line per dim) and
// drop any expanded-tree state. Transform/range/overlays are the user's view
// prefs and are left untouched. Rebuilds the card (so Reset hides again) + chart.
function resetSelection() {
  if (!state.current) return;
  state.selection = defaultSelection(state.current.meta);
  state.expanded = {};
  state.autoDefault = true;
  rerenderBreakdown(true);
  syncUrl();
}
// `defer`: skip the default render+draw and return without painting. The
// deep-link restore (restoreFromUrl) uses this so we DON'T fire a throwaway
// /series fetch for the dataset defaults that then races the real selection's
// fetch over one HTTP/2 connection — on a slow/congested mobile pipe that race
// starves the real chart fetch and the chart paints blank (see git 2257920).
//
// Returns this navigation's ticket, or null when a later navigation superseded
// this one while its meta was loading: two quick clicks must end on the dataset
// clicked LAST, not on whichever response happens to arrive last.
async function openDataset(id, preselect?, defer?) {
  const live = nav.go();
  const meta = await fetchMeta(id);
  metaCache[id] = meta;   // cache the opened dataset (overlays/switch-back reuse it)
  if (!live()) return null;
  // #4: valid-combination index for empty-possibility gating (cached per id).
  state.current = { id, meta, combos: comboCache[id] || null };
  Object.assign(state, openedState(meta, preselect));   // default selection and transform, no overlays
  if (!state.current.combos && (meta.dim_order || []).length) buildCombos(id, meta.dim_order, live);
  state.view = "detail";
  if (defer) return live;   // caller applies URL state, then renders once — one /series fetch, not two
  writeUrl(sharePath() + renderParams(), true);   // a new place: Back returns to the page before
  renderView();
  return live;
}
// #4: distinct dim-tuples that actually carry a value, from one unfiltered
// /series fetch. The single source of truth for which combinations exist — the
// per-level `data` flag can't express cross-dim gaps. Cached; refreshes the
// Breakdown card when ready so gating appears without blocking the first paint,
// unless the visitor has navigated since (`live`, the opening navigation's ticket).
const comboCache = {};
let combosPending = 0;   // in-flight /combos loads, for the e2e snapshot (window.__ds)
async function buildCombos(id, dimOrder, live) {
  combosPending++;
  try {
    const dims = dimOrder || [];
    // /combos returns the DISTINCT data-bearing dim-tuples server-side (columnar,
    // dim columns only) - a few hundred rows, vs the whole ~5 MB series we used
    // to pull and dedupe client-side. Each index is already one unique combo.
    const data = await jget(`/dataset/${id}/combos`);
    const col = dims.find(d => data && data[d]);
    const n = col ? data[col].length : 0;
    const combos = [];
    for (let r = 0; r < n; r++) {
      const t = {};
      // Coerce to string: level codes and selection are always strings, but the DuckDB
      // serving engine returns numeric-looking dim codes (e.g. the FSO "1/2/3" measure)
      // as JSON numbers. Without this, the gate is `["1"].includes(1)` === false and every
      // level greys out as "no data for this combination" (plumber.R returns strings, so
      // it only bites on the production engine).
      for (const d of dims) t[d] = data[d] != null && data[d][r] != null ? String(data[d][r]) : undefined;
      combos.push(t);
    }
    comboCache[id] = combos;
    if (live()) {
      state.current.combos = combos;
      // Surface gating now. Only the rows are rebuilt, keeping each tree's
      // scroll position; replacing the whole card sent it back to the top.
      if (state.view === "detail") updateDimResults();
    }
  } catch (e) { /* gating stays off — levelPossible returns true */ }
  finally { combosPending--; }
}

// Load the prebuilt search index — every selectable sub-series (the split dim's
// leaves) across all datasets, so search surfaces parts, not just dataset titles.
// One ~22 KB request instead of fetching all ~53 metas; built/served by the API
// (ds_search_index, memoised on the data's mtime). See dev/search-index.md.
// Promise-cached so the idle preload and an on-demand call (results page) share
// one in-flight request and never fetch twice; a failed load can retry.
let subIndexPromise = null;
function buildSubIndex() {
  if (state.subReady) return Promise.resolve();
  if (!subIndexPromise) {
    // The words are extra recall, not a requirement: a failed fetch leaves search
    // on titles and sub-series, as before.
    const words = jget(`/search-words.json`).catch(() => null);
    subIndexPromise = jget(`/search-index.json`)
      .then(async ix => { state.searchWords = await words; state.subIndex = ix; state.subReady = true; refreshOpenDropdowns(); })
      .catch(e => { subIndexPromise = null; /* allow a later retry; search degrades to dataset titles meanwhile */ });
  }
  return subIndexPromise;
}
// Fetch-and-cache ONE dataset's meta on demand. metaCache used to be filled for
// every dataset by buildSubIndex; now an overlay (or a deep link restoring one)
// pulls only the meta it actually needs — "load what you use", not all of it.
async function ensureMeta(id) {
  if (!metaCache[id]) {
    try { metaCache[id] = await fetchMeta(id); }
    catch (e) { return null; }
  }
  return metaCache[id];
}
// Shared grouped search (SEARCH_SPEC §2) — pure impl in ./lib/search; this binds
// it to the live catalog + sub-index + featured order.
const searchGrouped = (q, excludeId) => searchGroupedPure(q, excludeId, {
  catalog: state.catalog, subIndex: state.subIndex || [], subReady: !!state.subReady,
  featuredIds: mainDatasets(), label, badgeOf: badgeFor, maps: state.maps, words: state.searchWords || undefined,
});
// One dropdown row (SEARCH_SPEC §2 grammar). `plus` adds the trailing +/✓ action.
function dropItemHTML(r, plus) {
  if (r.mapOnly) return mapOnlyItemHTML(r, plus);
  const on = plus && isExtra({ datasetId: r.id, code: r.isSub ? r.subcode : (metaCache[r.id] && metaCache[r.id].default ? metaCache[r.id].default[splitDimOf(metaCache[r.id])] : null), isSub: r.isSub });
  const lead = r.depth ? `<span class="switcher-subtick"></span>` : badgeCodeHTML(r.badge);
  const data = `data-id="${r.id}"${r.isSub ? ` data-subdim="${r.subdim}" data-subcode="${esc(r.subcode)}" data-sublabel="${esc(r.title)}"` : ""}`;
  return `<div class="switcher-item ${r.depth ? "switcher-sub" : ""} ${r.hasSubMatch ? "switcher-parent" : ""} ${on ? "on" : ""}">
    <button class="switcher-open" ${data}>${lead}<span class="switcher-t">${esc(r.title)}</span><span class="switcher-meta">${esc(r.freq)}</span></button>
    ${plus ? (on || state.extras.length < MAX_EXTRAS
      ? `<button class="switcher-plus ${on ? "on" : ""}" ${data} title="${on ? t("Click to remove from the chart") : t("Add to the chart")}">${on ? CHECK : PLUS}</button>`
      : `<button class="switcher-plus" ${data} disabled title="${esc(tn("Up to {n} comparison series", MAX_EXTRAS))}">${PLUS}</button>`) : ""}
  </div>`;
}

// A map-only row: the whole row is the link to the map. No +, a map cannot go
// on a line chart; the empty slot keeps the frequencies in one column.
function mapOnlyItemHTML(r, plus) {
  return `<div class="switcher-item switcher-maponly">
    <a class="switcher-open" href="${esc(mapHref(r.map))}">${badgeCodeHTML(r.badge)}<span class="switcher-t">${esc(r.title)}</span><span class="switcher-meta">${mapMarkHTML(r.mapLevels)}${esc(r.freq)}</span></a>
    ${plus ? `<span class="switcher-plus-slot"></span>` : ""}
  </div>`;
}

// Render a list of dropdown rows, inserting the "Maps" and "All datasets"
// sub-headings before the first row of each block (empty-query state, SEARCH §default).
function dropRowsHTML(rows, plus) {
  return rows.map(r =>
    (r.mapsStart ? `<div class="switcher-hint switcher-hint-sub">${t("Maps")}</div>` : "") +
    (r.restStart ? `<div class="switcher-hint switcher-hint-sub">${t("All datasets")}</div>` : "") +
    dropItemHTML(r, plus)).join("");
}
// Keyboard navigation for a search input + its dropdown: ↓ opens it / moves the
// highlight down, ↑ moves up, Enter activates the highlighted row. `openFn`
// (re)renders the dropdown. Shared by the landing and chart-page search bars.
function dropdownKeydown(inp, drop, openFn) {
  return e => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Enter") return;
    const open = () => !drop.classList.contains("hidden");
    if (e.key === "Enter") {
      const cur = open() && drop.querySelector(".switcher-item.kbd-active .switcher-open");
      if (cur) { e.preventDefault(); cur.click(); }
      return;
    }
    e.preventDefault();
    if (!open()) openFn();
    const items = [...$$(".switcher-item", drop)];
    if (!items.length) return;
    let i = items.findIndex(el => el.classList.contains("kbd-active"));
    i = e.key === "ArrowDown" ? (i < 0 ? 0 : Math.min(items.length - 1, i + 1)) : (i <= 0 ? 0 : i - 1);
    items.forEach(el => el.classList.remove("kbd-active"));
    items[i].classList.add("kbd-active");
    items[i].scrollIntoView({ block: "nearest" });
  };
}

// ---- overlay extras (series from other datasets on the same chart) ----------
const extraKey = e => e.isSub ? `sub:${e.datasetId}:${e.code}` : `ds:${e.datasetId}`;
const isExtra = e => state.extras.some(x => extraKey(x) === extraKey(e));
// Build an extra record from a whole dataset (its default series) or a sub-series.
function datasetExtra(id) {
  const m = metaCache[id], cat: Partial<CatalogItem> = state.catalog.find(c => c.id === id) || {};
  const split = m ? splitDimOf(m) : null;
  const code = split && m.default ? m.default[split] : null;
  return { datasetId: id, datasetTitle: label((m && m.title) || cat.title) || id, badge: badgeFor(id),
    dim: split, code, label: label((m && m.title) || cat.title) || id, isSub: false };
}
function toggleExtra(e) {
  const k = extraKey(e);
  if (state.extras.some(x => extraKey(x) === k)) state.extras = state.extras.filter(x => extraKey(x) !== k);
  else if (state.extras.length < MAX_EXTRAS) state.extras.push(e);
}
function removeExtra(e) {
  const k = extraKey(e);
  state.extras = state.extras.filter(x => extraKey(x) !== k);
  drawChart();
}
// Overlays usually have different units/frequencies than the base, so a raw
// "level" overlay reads as a flat line against one shared y-axis. When the first
// overlay is added, switch to the indexed view (= 100 at start) so the shapes
// are comparable. The user can flip back to Level any time.
function maybeAutoIndex() {
  if (state.extras.length && state.transform === "level") {
    state.transform = "index";
    $$("#seg .seg-btn").forEach(x => x.classList.toggle("on", x.dataset.t === "index"));
  }
}
const levelLabel = (d, code) => levelLabelOf(state.current.meta, d, code);
// Colour shown on a breakdown row's toggle. Only the colour-carrying dim (the
// 1st varying line dim, or the split dim before anything varies) gets a colour;
// the dash dim and the 3+-varying fallback show a neutral selected state, since
// there one level maps to several differently-coloured lines.
function rowColorFor(d, code) {
  const vd = varyingDims();
  if (vd.length >= 3) return "";
  const colourDim = vd[0] || state.splitDim;
  if (d !== colourDim) return "";
  const sel = state.selection[colourDim] || [];
  const i = sel.indexOf(code);
  return SERIES_COLORS[(i < 0 ? 0 : i) % SERIES_COLORS.length];
}
const dimsParam = () => buildDimsParam(state.current.meta.dim_order || [], state.selection);
// The chart's display title — a user override (editable, URL-persisted) or the
// name of what is selected (seriesTitle). Used for the header, single-series
// legend, the tab title and the file names.
const autoTitle = () => (state.current && seriesTitle(state.current.meta, state.selection)) || "";
const chartTitle = () => (state.current && (state.title || autoTitle())) || "";

// ---- valid-combination gating (#4: hide empty possibilities) ----------------
// True iff some VALID data tuple has tuple[dim]===level given the current
// selection. Which other dims must match depends on what we're testing:
//  - the SPLIT dim's leaves honour every crossing (single-select) dim, so a
//    section dims out when the chosen type/adjustment has no data for it;
//  - a CROSSING dim's options honour only the split (ANY selected split code =
//    OR), NOT sibling crossing dims — a conflicting sibling is auto-corrected on
//    click rather than blocked, so we don't pre-disable a correctable option.
// Fully data-driven from state.current.combos — no per-dataset hardcoding.
// Returns true whenever no combo index is available (graceful no-op). Only the
// OTHER single-select (exclusive) dims constrain a level: line dims are free
// because the chart draws their cross-product and just skips empty cells, so a
// line level is greyed only when it has no data under the current exclusive
// picks, and an exclusive option is greyed only when no sibling exclusive
// combination admits it.
function levelPossible(dim, level) {
  const cur = state.current;
  if (!cur) return true;
  return levelPossiblePure(cur.combos, singleSelectDims(cur.meta), state.selection, dim, level);
}
// ---- shareable deep links ---------------------------------------------------
// Hash-based (#/d/<id>?dims=&t=&add=) so static hosting (Caddy, GitHub Pages)
// needs no server routing. Reuses the exact dims grammar /series already speaks.
function shareHash() {
  if (state.view !== "detail" || !state.current) return "#/";
  // Visible time window (#8): emit only when actually zoomed in (mirrors
  // drawChart's threshold) so full-span links stay clean. Stored as raw
  // period-start dates (the half-period display shift is re-applied on load).
  // A range preset is written as such (r=5y), so a shared or embedded chart
  // keeps showing the last five years as data arrives.
  const preset = /^\d+$/.test(state.range || "") ? state.range : null;
  const win = !preset && state.lastSeries
    ? zoomedRaw(chartScale(), state.lastSeries.dates, periodMidShiftSec(state.current.meta.frequency) || 0) : null;
  return buildShareHash({
    id: state.current.id, dimsParam: dimsParam(), transform: state.transform,
    defaultTransform: defaultTransform(state.current.meta.frequency, state.current.meta.default_transform),
    extras: state.extras.map(extraKey), title: state.title, window: win, preset,
  });
}
// Canonical share URL is now a real path (/d/<id>?…), not a hash — reuse the exact
// query-building in shareHash() and just drop the leading '#'.
// The shareable path keeps the language prefix (/de/d/<id>) whenever the UI is
// not English. Without it syncUrl's replaceState silently rewrote a localized
// URL back to the bare /d/<id> on hydration, so copying the address bar of a
// German page handed the recipient whatever THEIR browser language was — the
// localized routes existed but never survived a visit.
function sharePath() {
  const h = shareHash();
  const p = h === "#/" ? "/" : h.slice(1);
  const loc = getLocale();
  return loc === "en" || !p.startsWith("/d/") ? p : `/${loc}${p}`;
}
function shareUrl() { return location.origin + sharePath(); }
// Render-mode params (embed, theme) are NOT part of the shareable URL — the
// "dataseries.org ↗" link out of an embed must lead to the full page. But they
// DO have to survive syncUrl's rewrite: an iframe that reloads (or is restored
// from the bfcache) re-reads its own address, and without these it would come
// back as the full, light site inside the frame.
function renderParams() {
  const p = new URLSearchParams();
  if (state.embed) p.set("embed", "1");
  const t = pinnedTheme();
  if (t) p.set("theme", t);
  const qs = p.toString();
  return qs ? (sharePath().includes("?") ? "&" : "?") + qs : "";
}
// Live-sync the address bar without spamming history (replaceState, not push).
function syncUrl() {
  if (state.view === "detail" && state.current) writeUrl(sharePath() + renderParams(), false);
}
const getRoute = () => parseRoute(location.pathname, location.search, location.hash);
// Resolve overlay URL keys ("ds:id" / "sub:id:code") to extra records drawChart()
// can refetch — the string parse is in lib/route (parseExtraKey); the meta/catalog
// lookups (labels, split dim) stay here where the caches live.
function parseExtrasStr(addStr) {
  const out = [];
  for (const key of (addStr || "").split(",").filter(Boolean)) {
    const ref = parseExtraKey(key);
    if (!ref) continue;
    if (ref.kind === "sub") {
      const { datasetId, code } = ref;
      const m = metaCache[datasetId], split = m ? splitDimOf(m) : null;
      const lv = split && m && m.dimensions[split] && m.dimensions[split].levels[code];
      out.push({ datasetId, datasetTitle: label((m && m.title)) || datasetId, badge: badgeFor(datasetId),
        dim: split, code, label: (lv && label(lv.label)) || code, isSub: true });
    } else {
      const did = ref.datasetId;
      out.push(metaCache[did] ? datasetExtra(did)
        : { datasetId: did, datasetTitle: label((state.catalog.find(c => c.id === did) || {}).title) || did, badge: badgeFor(did), isSub: false });
    }
  }
  return out;
}
async function restoreFromUrl() {
  const r = getRoute();
  if (!r || !state.catalog.some(c => c.id === r.id)) return false;
  // clean baseline; defer the render so we draw once, below. Superseded (a later
  // navigation won the race): that one renders, this one must not touch state.
  const live = await openDataset(r.id, undefined, true);
  if (!live) return true;
  // What the URL asks for, reduced to what this dataset can draw (lib/selection).
  const v = viewFromRoute(r, state.current.meta, new Set(state.catalog.map(c => c.id)));
  state.selection = v.selection;
  state.autoDefault = !v.explicit;
  state.transform = v.transform;
  if (v.extras.length) {
    // Overlays carry only ids in the URL; fetch each referenced dataset's meta
    // (for its default dims + labels) before resolving them — buildSubIndex no
    // longer preloads every meta.
    await Promise.all([...new Set(v.extras.map(k => parseExtraKey(k).datasetId))].map(ensureMeta));
    if (!live()) return true;
    state.extras = parseExtrasStr(v.extras.join(","));
  }
  state.title = v.title;
  if (v.preset) state.range = v.preset;
  else if (v.window) {
    const sh = periodMidShiftSec(state.current.meta.frequency) || 0;   // store raw, display shifted
    state.pendingRange = { min: v.window[0] + sh, max: v.window[1] + sh };
    state.range = "custom";   // a window of its own: no preset button claims it
  }
  state.embed = !!r.embed;
  if (state.embed) {
    document.body.classList.add("embed");
    initEmbedTheme();   // ?theme= / postMessage / prefers-color-scheme (lib/theme.ts)
  }
  await renderDetail($("#view"));   // single draw, real selection; await so init defers the search index until after first paint
  // Only now can a parent's theme message actually be applied — announce.
  if (state.embed) notifyEmbedReady();
  return true;
}

// ---- detail -----------------------------------------------------------------
function renderDetail(v) {
  // Also set here, not only in renderView(): the deep-link restore path calls
  // renderDetail() directly, so a page opened at /d/<id> never passes through
  // renderView() and would otherwise keep the narrow header.
  document.body.classList.add("wide");
  const m = state.current.meta, id = state.current.id;
  v.innerHTML = `<main class="wrap detail-wrap fade-up">
    <div class="detail-grid">
      <div class="detail-main">
        <div id="switcher" class="switcher"></div>
        <p class="detail-desc">${esc(sourceName(m))} · ${esc(label(m.topic) || freqLabel(m.frequency))}</p>
        <div class="chart-card">
          <div class="chart-bar">
            <div class="chart-stat-unit-lbl" id="unit-lbl"></div>
            <div class="chart-tools"><div class="seg" id="seg">
              ${transformButtonsHTML()}
            </div>
            <div class="seg seg-range" id="range-seg">
              ${[["1",t("1Y")],["5",t("5Y")],["10",t("10Y")],["all",t("Max")]].map(([r,l]) =>
                `<button class="seg-btn ${(state.range||"all")===r?"on":""}" data-r="${r}">${l}</button>`).join("")}
            </div>
            </div>
          </div>
          <div id="chart"></div>
          <div class="legend" id="legend"></div>
          ${state.embed ? `<p class="embed-attrib">${esc(t("Source"))}: ${esc(sourceName(m))} · <a href="${esc(shareUrl())}" target="_blank" rel="noopener">dataseries.org ↗</a></p>` : ""}
        </div>
      </div>
      <aside class="detail-rail panel">
        ${railBreakdownHTML()}
        ${railDetailsHTML()}
        ${railAccessHTML()}
        ${railDownloadHTML()}
      </aside>
    </div>
  </main>`;
  wireTransformButtons();
  $$("#range-seg .seg-btn").forEach(b => b.onclick = () => {
    state.range = b.dataset.r;
    $$("#range-seg .seg-btn").forEach(x => x.classList.toggle("on", x.dataset.r === state.range));
    applyRange();   // setScale only — no redraw; persists across transform toggles (chart.ts keeps the window)
    syncUrl();      // reflect the chosen window (r=) in the shareable URL
  });
  wireRail();
  renderSwitcher();
  const drawn = drawChart();
  // Embeds tell their host how tall they are (lib/embed-height.ts). Re-armed on
  // every render: this replaced the element the previous observer was watching.
  // Only once the chart has drawn — while it is still "Loading…" the content is
  // ~330 px, and a host that applied that would collapse the frame and then grow
  // it again. The first number a host sees should be the real one.
  if (state.embed) drawn.then(() => watchEmbedHeight($("main.detail-wrap")));
  return drawn;   // returned so deep-link restore can await first paint before kicking off background work
}

function collapsibleCard(key, title, bodyHTML, opts) {
  const o = opts || {};
  const open = state.openCards[key];
  const marker = o.icon
    ? `<span class="rail-marker" style="background:${o.tint};color:${o.color}">${svgIcon(o.icon)}</span>` : "";
  return `<div class="rail-card collapsible ${open ? "open" : ""}"${o.id ? ` id="${o.id}"` : ""} data-card="${key}">
    <button class="rail-toggle">${marker}<span class="rail-h">${title}</span>${o.action || ""}<span class="rail-chev">${ICON.chev}</span></button>
    <div class="rail-body">${bodyHTML}</div></div>`;
}

// Rebuild only the Breakdown card (selection/expand changes) — never the whole
// page — then optionally redraw the chart. Keeps the header/hero/title/zoom put.
function rerenderBreakdown(redraw) {
  const card = document.getElementById("rail-breakdown");
  if (card) { const tmp = document.createElement("div"); tmp.innerHTML = railBreakdownHTML(); card.replaceWith(tmp.firstElementChild); }
  wireRail();
  if (redraw) drawChart();
}

// ---- series switcher: switch datasets OR add overlays; Main datasets when empty
function renderSwitcher() {
  const c = document.getElementById("switcher");
  if (!c) return;
  const nchip = extrasChipHTML();
  // Editable, URL-persisted plot title + an always-visible landing-style search
  // bar (placeholder "search") that opens datasets or adds them as overlays.
  c.innerHTML = `<div class="detail-search" id="detail-search"><form class="search search-big" id="detail-form">
      <span class="search-ic">${ICON.search}</span>
      <input id="detail-q" placeholder="${t('Search…')}" spellcheck="false" autocomplete="off">
      <button type="button" class="search-clear hidden" id="detail-clear" aria-label="Clear">${CLOSE}</button>
      <button type="submit" class="search-go search-go-minimal" aria-label="Search">${ICON.arrow}</button>
    </form><div class="switcher-list hero-drop hidden" id="detail-drop"></div></div>
    <div class="switcher-titlebar">
      <span class="switcher-title-t" id="sw-title" contenteditable="true" spellcheck="false" title="${t('Click to rename this chart')}">${esc(chartTitle())}</span>${nchip}
      <button class="title-share" id="title-share" title="${t('Copy a link to this chart')}" aria-label="${t('Copy a link to this chart')}">${ICON.link}</button>
      ${mapPillHTML()}
    </div>`;
  wireDetailTitle();
  wireDetailShare();
  wireDetailSearch();
}
// "+N" beside the title: how many comparison series are on the chart. Redrawn
// with the legend (syncExtrasChip), since overlays come and go without a
// re-render of the switcher.
function extrasChipHTML() {
  const n = state.extras.length;
  return n ? `<span class="switcher-count" title="${esc(tn("{n} comparison series on the chart", n))}">+${n}</span>` : "";
}
function syncExtrasChip() {
  const title = $("#sw-title"); if (!title) return;
  const old = $(".switcher-count"); if (old) old.remove();
  title.insertAdjacentHTML("afterend", extrasChipHTML());
}
function wireDetailShare() {
  const btn = $("#title-share"); if (!btn) return;
  btn.onclick = async () => {
    const url = shareUrl();
    try { await navigator.clipboard.writeText(url); }
    catch (e) { const t = document.createElement("textarea"); t.value = url; document.body.appendChild(t); t.select(); try { document.execCommand("copy"); } catch (_) {} t.remove(); }
    btn.classList.add("copied");
    btn.innerHTML = CHECK;
    setTimeout(() => { btn.classList.remove("copied"); btn.innerHTML = ICON.link; }, 1400);
  };
}
function wireDetailTitle() {
  const t = $("#sw-title"); if (!t) return;
  const commit = () => {
    const v = t.textContent.trim();
    state.title = (v && v !== autoTitle()) ? v : null;
    if (!v) t.textContent = chartTitle();   // never leave it blank
    syncUrl(); drawChart();                  // reflect in legend/PNG/SVG + URL
  };
  t.onblur = commit;
  t.onkeydown = e => { if (e.key === "Enter") { e.preventDefault(); t.blur(); } };
}
function wireDetailSearch() {
  const inp = $("#detail-q"), drop = $("#detail-drop"), wrap = $("#detail-search"), clear = $("#detail-clear");
  if (!inp) return;
  const renderDrop = () => {
    const q = inp.value.trim();
    const rows = searchGrouped(q, state.current.id);
    drop.classList.remove("hidden");
    drop.innerHTML = `<div class="switcher-hint">${q ? t("Results") : t("Main datasets")}<span class="switcher-hint-key">${t("click to open")} · <b>+</b></span></div>` +
      (rows.length ? dropRowsHTML(q ? rows.slice(0, 50) : rows, true) : `<div class="switcher-empty">No series match “${esc(q)}”</div>`);
    $$("button.switcher-open", drop).forEach(el => el.onclick = () =>
      el.dataset.subcode != null ? openDataset(el.dataset.id, { dim: el.dataset.subdim, code: el.dataset.subcode }) : openDataset(el.dataset.id));
    $$(".switcher-plus", drop).forEach(el => el.onclick = async () => {
      const live = nav.current();
      await ensureMeta(el.dataset.id);   // overlay needs its default dims/split
      if (!live()) return;
      const ex = el.dataset.subcode != null
        ? { datasetId: el.dataset.id, datasetTitle: label((state.catalog.find(c => c.id === el.dataset.id) || {}).title), badge: badgeFor(el.dataset.id), dim: el.dataset.subdim, code: el.dataset.subcode, label: el.dataset.sublabel, isSub: true }
        : datasetExtra(el.dataset.id);
      toggleExtra(ex); maybeAutoIndex(); drawChart(); renderDrop(); inp.focus();
    });
  };
  const sync = () => { renderDrop(); if (clear) clear.classList.toggle("hidden", !inp.value); };
  inp.onfocus = renderDrop;
  inp.oninput = sync;
  inp.onkeydown = dropdownKeydown(inp, drop, renderDrop);
  if (clear) clear.onclick = () => { inp.value = ""; clear.classList.add("hidden"); inp.focus(); renderDrop(); };
  $("#detail-form").onsubmit = e => { e.preventDefault(); state.query = inp.value.trim(); goView("results"); };
  docListener("detail-out", "mousedown", e => { if (wrap && !wrap.contains(e.target)) drop.classList.add("hidden"); });
  docListener("detail-esc", "keydown", e => { if (e.key === "Escape") drop.classList.add("hidden"); });
}
function railBreakdownHTML() {
  const m = state.current.meta, order = m.dim_order || [];
  if (!order.length) return "";
  const inner = `<div class="dim-wrap"><div id="dim-results">${dimResultsHTML()}</div></div>`;
  // Muted reset glyph in the card header — only once the selection has diverged
  // from the dataset's factory default (updateDimResults toggles `.show` live).
  const action = `<span class="rail-action${selectionIsDefault() ? "" : " show"}" id="dim-reset" role="button" tabindex="0"
     title="${t('Reset to defaults')}" aria-label="${t('Reset to defaults')}">${RESET}</span>`;
  return collapsibleCard("breakdown", t("Breakdown"), inner,
    { id: "rail-breakdown", icon: RAIL_ICONS.compare, color: "#2E6B76", tint: "#E6EEEF", action });
}
// Body of the breakdown: every line-capable dim as a multi-select menu (tree if
// hierarchical, else flat rows) — more than one can be open at once — and every
// single-select (exclusive) dim as a segmented "pick one" control.
function dimResultsHTML() {
  const m = state.current.meta, order = m.dim_order || [];
  const lds = new Set(lineDims(m));
  // Multi-select (line) dims first, single-select (pick-one) filters beneath —
  // the chart's line drivers stay grouped at the top regardless of dim_order.
  // Order within each group still follows dim_order. Channel assignment
  // (colour/dash) keys off dim_order in drawChart, so it's unaffected by this.
  let body = "";
  for (const d of order) if (lds.has(d)) body += multiDimHTML(d);
  for (const d of order) if (!lds.has(d)) body += filterSeg(d);
  return body;
}
// One line-capable dim as a multi-select block: header + hierarchical tree or
// flat rows in the same row grammar. Toggling a row adds/removes a line level.
function multiDimHTML(d) {
  const dim = state.current.meta.dimensions[d];
  // data-dim keys the scroll-position carry-over in updateDimResults: the rebuild
  // throws these nodes away, so each tree has to be matched to its successor.
  return `<div class="dim-chips-h">${esc(label(dim.label))}</div>`
    + (dim.hierarchy ? `<div class="dim-tree" data-dim="${d}">${treeHTML(d, dim)}</div>`
    : `<div class="dim-tree flat" data-dim="${d}">${flatRowsHTML(d, dim)}</div>`);
}
// Single-select (exclusive) dimension as a segmented control — the "pick one"
// signal that plain chips lacked. Long sentence labels wrap within the track.
// Keeps data-fdim/data-code so wireRail's existing [data-fdim] handler applies.
function filterSeg(d) {
  const dim = state.current.meta.dimensions[d];
  const codes = Object.keys(dim.levels);
  return `<div class="dim-chips-h">${esc(label(dim.label))}</div>
    <div class="seg dim-seg" role="radiogroup" aria-label="${esc(label(dim.label))}">${codes.map(c => {
      const on = (state.selection[d] || []).includes(c);
      const dis = !levelPossible(d, c);
      return `<button class="seg-btn ${on ? "on" : ""}${dis ? " dis" : ""}" data-fdim="${d}" data-code="${c}" role="radio" aria-checked="${on}"${dis ? ` title="No data for this combination" aria-disabled="true"` : ""}>${esc(levelLabel(d, c))}</button>`;
    }).join("")}</div>`;
}
// Rebuilding #dim-results discards every .dim-tree, and a discarded scroller
// takes its scrollTop with it — so a plain innerHTML swap silently sent each
// tree back to the top and the row the user just tapped leapt down the screen.
// Carry the offsets across, then pin `anchorSel` (the row/caret that triggered
// the rebuild) to the exact screen position it held before it. Expanding then
// does the one thing it should: add rows below, nothing moves.
function updateDimResults(anchorSel?: string) {
  const el = $("#dim-results");
  if (el) {
    const tops = new Map();
    $$(".dim-tree", el).forEach(tr => tops.set(tr.dataset.dim, tr.scrollTop));
    const y0 = anchorSel && el.querySelector(anchorSel)
      ? el.querySelector(anchorSel).getBoundingClientRect().top : null;
    el.innerHTML = dimResultsHTML();
    $$(".dim-tree", el).forEach(tr => { const v = tops.get(tr.dataset.dim); if (v) tr.scrollTop = v; });
    if (y0 != null) pinRowTo(el.querySelector(anchorSel), y0);
    wireRail();
  }
  // The reset glyph lives in the (un-rebuilt) card header — reflect dirty state here.
  const rb = $("#dim-reset");
  if (rb) rb.classList.toggle("show", !selectionIsDefault());
}
// Put `el` back at viewport y `y0`. Collapsing can shrink a tree enough that the
// restored scrollTop gets clamped, so spend the correction on the tree's own
// scroller first and only fall through to the page (the stacked/mobile layout,
// where the tree has no scroller at all, takes that branch directly).
function pinRowTo(el, y0) {
  if (!el) return;
  const sc = el.closest(".dim-tree");
  if (sc && sc.scrollHeight > sc.clientHeight) sc.scrollTop += el.getBoundingClientRect().top - y0;
  const rest = el.getBoundingClientRect().top - y0;
  if (Math.abs(rest) > 0.5) window.scrollBy(0, rest);
}
// A row/caret selector safe to hand to querySelector after the rebuild.
const anchorFor = (attr, key) => `[${attr}="${String(key).replace(/["\\]/g, "\\$&")}"]`;
// Collapsible tree with the §6 grammar: caret to expand/collapse, connector tick
// on child leaves, normal-case group headers, trailing right-edge colour toggle.
// The row toggles selection (multi-select); the caret toggles expand.
function treeHTML(d, dim) {
  const levels = dim.levels || {};
  const exp = state.expanded[d] || (state.expanded[d] = (() => {
    const s = new Set(Object.keys(dim.hierarchy));
    for (const code of (state.selection[d] || [])) for (const a of ancestorsOf(dim.hierarchy, code)) s.add(a);
    return s;
  })());
  const walk = (node, depth) => Object.entries(node).map(([code, kids]) => {
    const hasKids = Object.keys(kids).length > 0;
    const info = levels[code] || {};
    const selectable = info.data !== false;
    const isOpen = exp.has(code);
    // Leaves carry no glyph at all: the padding already shows the nesting, and
    // the old connector tick (plus the invisible caret-width spacer at depth 0)
    // added a second alignment column that did no work. Only nodes with children
    // keep a caret, which hangs to the left of the label like a bullet.
    const pad = (hasKids ? 12 : 14) + depth * 20;
    const lead = hasKids ? `<span class="dim-caret ${isOpen ? "open" : ""}" data-exp="${d}|${code}">${CARET}</span>` : "";
    let row;
    if (!selectable) {
      row = `<div class="dim-group" data-grpexp="${d}|${code}" style="padding-left:${pad}px">${lead}${esc(label(info.label) || code)}</div>`;
    } else {
      const on = (state.selection[d] || []).includes(code);
      const col = on ? (rowColorFor(d, code) || NEUTRAL_ON) : "";
      const dis = !on && !levelPossible(d, code);
      const tip = dis ? t("No data with the current type/adjustment") : (on ? t("Click to remove from the chart") : t("Add to the chart"));
      row = `<button class="dim-row ${on ? "on" : ""}${dis ? " dis" : ""}" data-dim="${d}" data-code="${code}" style="padding-left:${pad}px" title="${tip}">${lead}<span class="dim-label">${esc(label(info.label) || code)}</span>${dimToggle(on, col)}</button>`;
    }
    return row + (hasKids && isOpen ? walk(kids, depth + 1) : "");
  }).join("");
  return walk(dim.hierarchy, 0);
}
// A flat (no-hierarchy) line dim rendered in the same row grammar as the tree:
// one row per level, no caret/tick, with the trailing colour toggle (multi-select).
function flatRowsHTML(d, dim) {
  const levels = dim.levels || {};
  return Object.keys(levels).filter(c => levels[c].data !== false).map(c => {
    const on = (state.selection[d] || []).includes(c);
    const col = on ? (rowColorFor(d, c) || NEUTRAL_ON) : "";
    const dis = !on && !levelPossible(d, c);
    const tip = dis ? t("No data with the current type/adjustment") : (on ? t("Click to remove from the chart") : t("Add to the chart"));
    return `<button class="dim-row ${on ? "on" : ""}${dis ? " dis" : ""}" data-dim="${d}" data-code="${c}" style="padding-left:10px" title="${tip}"><span class="dim-label">${esc(levelLabel(d, c))}</span>${dimToggle(on, col)}</button>`;
  }).join("");
}
function ancestorsOf(hierarchy, code) {
  const dfs = (node, trail) => {
    for (const [k, kids] of Object.entries(node)) {
      if (k === code) return trail;
      const r = dfs(kids, trail.concat(k));
      if (r) return r;
    }
    return null;
  };
  return dfs(hierarchy, []) || [];
}

function railDetailsHTML() {
  const m = state.current.meta;
  const inner = `<dl class="meta">
      <dt>${esc(t("Source"))}</dt><dd>${esc(sourceName(m))}</dd>
      <dt>${esc(t("Frequency"))}</dt><dd>${esc(freqLabel(m.frequency))}</dd>
      <dt>${esc(t("Topic"))}</dt><dd>${esc(label(m.topic) || "–")}</dd>
      <dt>${esc(t("Coverage"))}</dt><dd class="tnum">${esc((m.start||"").slice(0,4))} – ${esc((m.end||"").slice(0,4))}</dd>
      ${m.updated ? `<dt>${esc(t("Last updated"))}</dt><dd class="tnum">${esc(fmtDate(m.updated))}</dd>` : ""}
      ${m.fetched_utc ? `<dt>${esc(t("Checked"))}</dt><dd class="tnum">${esc(fmtDate(m.fetched_utc))}</dd>` : ""}
      <dt>${esc(t("Series"))}</dt><dd>${m.dim_order && m.dim_order.length ? esc(tn("{n} available", state.catalog.find(c=>c.id===state.current.id)?.n_series)) : esc(t("Single series"))}</dd>
    </dl>`;
  return collapsibleCard("details", t("Details"), inner, { icon: RAIL_ICONS.details, color: "#6B6258", tint: "#F0F2F4" });
}
// The series the chart currently shows, as {id, dims:{dim:[codes]}} specs:
// the base dataset (its split dim may carry several selected codes = several
// lines) plus one spec per overlaid extra dataset. Drives the Access snippets so
// copy-pasting reproduces exactly what's on screen.
function seriesSpecs() {
  const order = state.current.meta.dim_order || [];
  const base = {};
  for (const d of order) { const s = state.selection[d] || []; if (s.length) base[d] = s.slice(); }
  const specs = [{ id: state.current.id, dims: base }];
  for (const ex of state.extras) {
    const cm = metaCache[ex.datasetId] || {};
    const dims = {};
    for (const k in (cm.default || {})) dims[k] = [cm.default[k]];
    if (ex.dim && ex.code) dims[ex.dim] = [ex.code];
    specs.push({ id: ex.datasetId, dims });
  }
  return specs;
}
// dimsQuery, csvUrl imported from ./lib/api (the tidy-CSV door for Stata, MATLAB,
// Julia, Excel/Power Query, curl, … — same dims grammar as the chart and /series).
// Name the variable per series only when there's more than one on the chart.
const vname = (stem, i, n) => n > 1 ? `${stem}${i + 1}` : stem;
// Tiny dependency-free syntax highlighter for the code-export snippets. Wraps
// tokens in <span class="tok-*"> (GitHub/Shiki-style palette, see app.css).
// Conservative: comments → strings → numbers → keywords → known functions; any
// unmatched text stays plain. Operates on RAW snippet text and escapes every
// piece, so .textContent (used by Copy) still returns clean, span-free code.
function hl(code) {
  const TOK = /(#[^\n]*|(?<!:)\/\/[^\n]*)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')|(-?\b\d+(?:\.\d+)?\b)|\b(library|import|from|as|using|require|install\.packages|pak|filename|proc|clear|replace|run|delimited|defer)\b|\b(ds|readtable|CSV|DataFrames|Downloads|XLSX|curl|writeFile|request|req_url_query|req_perform|resp_body_json|requests|DataFrame|raise_for_status)\b/g;
  let out = "", last = 0, m;
  while ((m = TOK.exec(code)) !== null) {
    if (m[0].length === 0) { TOK.lastIndex++; continue; }
    out += esc(code.slice(last, m.index));
    const cls = m[1] ? "tok-com" : m[2] ? "tok-str" : m[3] ? "tok-num" : m[4] ? "tok-kw" : "tok-fn";
    out += `<span class="${cls}">${esc(m[0])}</span>`;
    last = m.index + m[0].length;
  }
  return out + esc(code.slice(last));
}
function accessSnippet(tab) {
  const specs = seriesSpecs();
  // The dataseries packages (CRAN, PyPI) wrap the API: ds() takes a dataset id
  // plus the dimension filters and returns a tidy data frame (date already a
  // Date / datetime). A single code is a string, a multi-code selection a vector
  // / list. Dimension names that are not identifiers are quoted (lib/snippets).
  if (tab === "r") return rSnippet(specs);
  if (tab === "python") return pythonSnippet(specs);
  // Every tool below reads the same tidy-CSV URL — no wrapper package, just a GET.
  const n = specs.length;
  if (tab === "curl") {
    return specs.map(s => `curl "${csvUrl(s)}"`).join("\n");
  }
  if (tab === "stata") {
    return specs.map((s, i) => `import delimited "${csvUrl(s)}", clear`
      + (n > 1 ? `   // series ${i + 1}` : "")).join("\n");
  }
  if (tab === "sas") {
    // Base SAS: the URL access method streams the CSV straight into PROC IMPORT.
    // Filerefs are limited to 8 characters, so keep the stem short.
    // SINGLE quotes are required: the URL contains "&dims=", and inside a
    // double-quoted SAS string "&dims" would be resolved as a macro variable.
    return specs.map((s, i) => {
      const ref = vname("ds", i, n);
      return `filename ${ref} url '${csvUrl(s)}';\n`
        + `proc import datafile=${ref} out=work.${ref} dbms=csv replace;\n`
        + `run;`;
    }).join("\n\n");
  }
  if (tab === "matlab") {
    return specs.map((s, i) => `${vname("T", i, n)} = readtable("${csvUrl(s)}");`).join("\n");
  }
  if (tab === "julia") {
    return "using CSV, DataFrames, Downloads\n" + specs.map((s, i) =>
      `${vname("df", i, n)} = CSV.read(Downloads.download("${csvUrl(s)}"), DataFrame)`).join("\n");
  }
  if (tab === "excel") {
    // Only the URL goes in the (copyable) code block; the how-to is in accessNote.
    return specs.map(s => csvUrl(s)).join("\n");
  }
  // embed: a live <iframe> of this exact chart (reuses the shareable URL in a
  // stripped ?embed mode — same-origin, no API/CORS work, auto-updates).
  return `<iframe\n  src="${embedUrl()}"\n  width="100%" height="${EMBED_HEIGHT}"\n  style="border:0;border-radius:12px"\n  loading="lazy"\n  title="${esc(chartTitle())} · dataseries.org">\n</iframe>`;
}
// Production embed URL for the current chart (current dims/transform/title), in
// the stripped ?embed render mode. Reuses shareHash's grammar verbatim.
// The real path, not the legacy #/d/ hash (which only redirects). A range
// preset travels as r=5y, so the embedded window follows new data.
function embedUrl() {
  const p = sharePath();
  return "https://dataseries.org" + p + (p.includes("?") ? "&" : "?") + "embed=1";
}
const ACCESS_TABS = [["r","R"],["python","Python"],["curl","cURL"],["stata","Stata"],["sas","SAS"],["matlab","MATLAB"],["julia","Julia"],["excel","Excel"],["embed","Embed"]];
const ACCESS_MORE = ACCESS_TABS.filter(([v]) => v !== "r" && v !== "python" && v !== "embed");
const accLabel = v => (ACCESS_TABS.find(t => t[0] === v) || ["", v])[1];
// Prose how-to for languages that need steps rather than copy-paste code. Kept
// separate from the (copyable) snippet so the code box holds only what you copy.
function accessNote(tab) {
  if (tab === "excel") return "In Excel: Data › Get Data › From Web, then paste the URL below. It refreshes in place when the data updates.";
  return "";
}
function railAccessHTML() {
  const inMore = ACCESS_MORE.some(([v]) => v === state.access);
  const caret = svgIcon(["M6 9l6 6 6-6"], 11, 2.4);
  const inner = `<div class="acc-head">
      <div class="seg acc-tabs">
        <button class="seg-btn ${state.access === "r" ? "on" : ""}" data-acc="r">R</button>
        <button class="seg-btn ${state.access === "python" ? "on" : ""}" data-acc="python">Python</button>
        <button class="seg-btn ${state.access === "embed" ? "on" : ""}" data-acc="embed">${esc(t("Embed"))}</button>
        <button class="seg-btn acc-more ${inMore ? "on" : ""}" id="acc-more-btn" aria-haspopup="true"><span class="acc-more-lbl">${esc(inMore ? accLabel(state.access) : t("More"))}</span>${caret}</button>
      </div>
      <div class="acc-menu hidden" id="acc-menu" role="menu">
        ${ACCESS_MORE.map(([v, l]) => `<button class="acc-menu-item ${state.access === v ? "on" : ""}" role="menuitem" data-acc="${v}">${l}</button>`).join("")}
      </div>
    </div>
    <p class="acc-note ${accessNote(state.access) ? "" : "hidden"}" id="acc-note">${esc(accessNote(state.access))}</p>
    <div class="code-block"><pre class="mono" id="acc-code">${hl(accessSnippet(state.access))}</pre><button class="copy" id="acc-copy">${esc(t("Copy"))}</button></div>`;
  return collapsibleCard("access", t("Use this series"), inner, { id: "rail-access", icon: RAIL_ICONS.code, color: "#2E6B76", tint: "#E9EFF0" });
}
// Refresh the code snippet + the prose note to the current language + selection.
function refreshAccess() {
  const r = $("#acc-code"); if (r) r.innerHTML = hl(accessSnippet(state.access));
  const n = $("#acc-note"); if (n) { const t = accessNote(state.access); n.textContent = t; n.classList.toggle("hidden", !t); }
}
function railDownloadHTML() {
  const inner = `<div class="dl-grid">
      <button class="dl-btn" data-dl="csv"><span class="dl-fmt">CSV</span><span class="dl-ext">.csv</span></button>
      <button class="dl-btn" data-dl="xlsx"><span class="dl-fmt">Excel</span><span class="dl-ext">.xlsx</span></button>
      <button class="dl-btn" data-dl="json"><span class="dl-fmt">JSON</span><span class="dl-ext">.json</span></button>
      <button class="dl-btn" data-dl="png"><span class="dl-fmt">PNG</span><span class="dl-ext">.png</span></button>
      <button class="dl-btn" data-dl="svg"><span class="dl-fmt">SVG</span><span class="dl-ext">.svg</span></button>
    </div>`;
  return collapsibleCard("download", t("Download"), inner, { icon: RAIL_ICONS.download, color: "#1f8a5b", tint: "#e7f4ee" });
}
function wireRail() {
  // collapse/expand a rail card in place (no rebuild, no chart redraw)
  $$(".rail-toggle").forEach(btn => btn.onclick = () => {
    const card = btn.closest(".collapsible") as any, key = card.dataset.card;
    state.openCards[key] = !state.openCards[key];
    card.classList.toggle("open", state.openCards[key]);
  });
  // caret / group header toggles expand (rebuild tree only, no chart redraw)
  const toggleExpand = (key, attr) => { const [d, code] = key.split("|"); const s = state.expanded[d]; if (s.has(code)) s.delete(code); else s.add(code); updateDimResults(anchorFor(attr, key)); };
  $$(".dim-row .dim-caret[data-exp]").forEach(el => el.onclick = e => { e.stopPropagation(); toggleExpand(el.dataset.exp, "data-exp"); });
  $$(".dim-group[data-grpexp]").forEach(el => el.onclick = () => toggleExpand(el.dataset.grpexp, "data-grpexp"));
  // a breakdown row (tree or flat) or split chip toggles its series (multi-select);
  // dimmed (no-data-for-current-settings) rows are not selectable (#4)
  $$(".dim-row[data-code], .split-chip").forEach(el => el.onclick = () => {
    if (el.classList.contains("dis")) return;
    toggleSplit(el.dataset.dim, el.dataset.code);
  });
  // exclusive (single-select) dim options: pick one, then auto-correct the other
  // crossing dims if the pick would empty the chart (#4). Truly-impossible
  // options are dimmed + blocked.
  $$("[data-fdim]").forEach(el => el.onclick = () => {
    if (el.classList.contains("dis")) return;
    state.autoDefault = false;
    state.selection = pickExclusive(state.current.meta, state.current.combos, state.selection, el.dataset.fdim, el.dataset.code);
    updateDimResults(`[data-fdim="${el.dataset.fdim}"][data-code="${el.dataset.code}"]`); drawChart();
  });
  // language tabs: R | Python | More(menu) — swap the snippet in place
  const accMenu = $("#acc-menu"), accMore = $("#acc-more-btn");
  const moreVals = new Set(ACCESS_MORE.map(([v]) => v));
  const setAccess = v => {
    state.access = v;
    $$(".acc-tabs [data-acc]").forEach(t => t.classList.toggle("on", t.dataset.acc === v));
    if (accMore) {
      const im = moreVals.has(v);
      accMore.classList.toggle("on", im);
      const lbl = accMore.querySelector(".acc-more-lbl"); if (lbl) lbl.textContent = im ? accLabel(v) : t("More");
    }
    $$("#acc-menu [data-acc]").forEach(t => t.classList.toggle("on", t.dataset.acc === v));
    if (accMenu) accMenu.classList.add("hidden");
    refreshAccess();
  };
  $$(".acc-tabs [data-acc], #acc-menu [data-acc]").forEach(b => b.onclick = () => setAccess(b.dataset.acc));
  if (accMore) accMore.onclick = e => { e.stopPropagation(); accMenu.classList.toggle("hidden"); };
  docListener("acc-out", "mousedown", e => { if (accMenu && !accMenu.classList.contains("hidden") && !e.target.closest(".acc-head")) accMenu.classList.add("hidden"); });
  const cp = $("#acc-copy");
  if (cp) cp.onclick = () => { try { navigator.clipboard.writeText($("#acc-code").textContent); cp.textContent = t("Copied"); setTimeout(() => cp.textContent = t("Copy"), 1300); } catch (e) {} };
  $$("[data-dl]").forEach(el => el.onclick = () => download(el.dataset.dl));
  const resetBtn = $("#dim-reset");
  if (resetBtn) {
    // Lives inside the collapse toggle — stop the click from also folding the card.
    resetBtn.onclick = e => { e.stopPropagation(); resetSelection(); };
    resetBtn.onkeydown = e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); resetSelection(); } };
  }
}
// Toggle a level of a line-capable dim (multi-select). A line dim always keeps
// at least one level. No hard cap — the cross-product is allowed to grow; the
// chart shows a soft warning past LINE_WARN lines (see drawChart).
function toggleSplit(d, code) {
  state.autoDefault = false;
  state.selection = toggleLevel(state.selection, d, code);
  updateDimResults(`.dim-row[data-dim="${d}"][data-code="${code}"]`);
  drawChart();
}
// Recover from a stale/invalid stored default, so the chart never opens empty.
// First keep what the stored default pins and take the other dims from the
// first row that has data (ch_ffa_finances: the curated debt ratio, with a
// real estimate type); failing that, the first row of the whole dataset
// (ch_snb_bopcapbalq, whose stored default matches nothing). The server page
// makes the same choice (ds_series_view in api/discover.R).
async function fallbackSelect() {
  const id = state.current.id, dims = state.current.meta.dim_order || [];
  const live = nav.current();
  const pinned = pinnedDefault(state.current.meta);
  try {
    for (const dp of [...new Set([pinned, ""])]) {
      const data = await jget(`/series?dataset=${id}` + (dp ? `&dims=${encodeURIComponent(dp)}` : ""));
      if (!live()) return;   // navigated away meanwhile
      const sel = firstRowSelection(data, dims);
      if (!sel) continue;
      Object.assign(state.selection, sel);
      if (state.view === "detail") { renderView(); }
      return;
    }
  } catch (e) { /* leave the empty message */ }
}

// ---- transforms + chart -----------------------------------------------------
// The view-transform math (level / pop / yoy / index) is pure and lives in
// lib/transforms.ts (mirrored server-side in ds_apply_transform). The wrappers
// below bind it to the live state (current frequency, index window, pctMode).
// A series in percent changes in percentage points (changeDiff, lib/meta).
const baseDiff = () => changeDiff(state.current.meta, state.selection);
const transformOptions = () => transformOptionsPure(state.current.meta.frequency, !!baseDiff());
const transformUnit = () => transformUnitPure({
  transform: state.transform,
  freq: state.current.meta.frequency,
  pctMode: state.pctMode,
  unit: unitOf(state.current.meta, state.selection),
  pp: !!baseDiff(),
});
const transformButtonsHTML = () => transformOptions().map(([v2, l]) =>
  `<button class="seg-btn ${state.transform === v2 ? "on" : ""}" data-t="${v2}">${l}</button>`).join("");
function wireTransformButtons() {
  $$("#seg .seg-btn").forEach(b => b.onclick = () => {
    state.transform = b.dataset.t;
    $$("#seg .seg-btn").forEach(x => x.classList.toggle("on", x.dataset.t === state.transform));
    drawChart();   // in-place: only the chart redraws, zoom preserved
  });
}
// The labels change when a selection moves between a series in percent and
// one that is not (registered unemployment has both).
function syncTransformButtons() {
  const seg = $("#seg"); if (!seg) return;
  const html = transformButtonsHTML();
  if (seg.innerHTML !== html) { seg.innerHTML = html; wireTransformButtons(); }
}
// The line above the chart, as FRED has it: "Jun 2026: 4.79 · Percent of the
// labour force · monthly". The latest observation only on a one-line chart;
// the unit caption and frequency always.
function statLineHTML(dates: string[], lines: { values: (number | null)[] }[]): string {
  const parts = [esc(transformUnit()), esc(freqLabel(state.current.meta.frequency))].filter(Boolean);
  const line = lines.length === 1 ? lines[0] : undefined;
  let i = line ? line.values.length - 1 : -1;
  while (line && i >= 0 && line.values[i] == null) i--;
  if (line && i >= 0) {
    const v = line.values[i] as number;
    const val = state.pctMode ? fmtStat(v * 100)
      : state.transform === "yoy" || state.transform === "pop" ? `${v > 0 ? "+" : ""}${fmtStat(v)}`
      : fmtStat(v);
    parts.unshift(`${esc(periodLabel(dates[i] as string, state.current.meta.frequency))}: <span class="chart-stat-v tnum">${esc(val)}</span>`);
  }
  return parts.join(" · ");
}
// Some datasets (e.g. FFA public finances) store ratio indicators as a 0-1 share
// of GDP. When only such levels are plotted in the level view (no overlays), show
// the axis as a percentage. The ratio levels are declared in the datasheet
// (## Display percent-levels -> meta.percent_levels), so this stays data-driven.
function pctMode() {
  const m = state.current && state.current.meta;
  if (!m || state.transform !== "level" || state.extras.length) return false;
  const pl = m.percent_levels;
  if (!pl || !pl.length || !state.splitDim) return false;
  const sel = state.selection[state.splitDim] || [];
  return sel.length > 0 && sel.every(c => pl.includes(c));
}

// Show the range preset on the drawn lines (chart.ts showRange). The window
// persists across transform toggles: drawChart keeps the preset on redraw.
function applyRange() {
  showRange(state.lastSeries && state.lastSeries.dates, state.range);
}

// Generation guard: drawChart is async, and several flows fire it twice in quick
// succession (two toggles, a transform flip during a load). Both calls fetch in
// parallel but read state.selection *after* their await, so the older call could
// filter its data by the newer selection, find nothing, and tear the chart down;
// whichever resolved last won. Each call takes a draw ticket (lib/navigation);
// after every await it bails if a newer draw or a navigation has started, so
// only the latest invocation may touch the plot / the DOM.
let drawsInFlight = 0;
// Read-only snapshot of what the chart shows, for the e2e specs (web/e2e/). The
// canvas can't be read back, and counting legend entries says nothing about the
// numbers, so the specs compare this against the API. `busy` is true while a
// drawChart or the combinations index (which rebuilds the Breakdown rows) is
// still loading; wait for it to clear before reading or clicking rows.
(window as any).__ds = {
  chart: () => ({
    busy: drawsInFlight > 0 || combosPending > 0,
    id: state.current && state.current.id,
    view: state.view,
    selection: JSON.parse(JSON.stringify(state.selection || {})),
    transform: state.transform,
    range: state.range,
    extras: (state.extras || []).map(extraKey),
    empty: !state.lastSeries,
    dates: state.lastSeries ? state.lastSeries.dates.slice() : [],
    series: state.lastSeries ? state.lastSeries.series.map(s => ({ label: s.label, color: s.color, dash: s.dash || [], values: s.values.slice() })) : [],
    xmin: hasChart() ? chartScale().min : null,
    xmax: hasChart() ? chartScale().max : null,
    xshift: chartShift(),
  }),
};
// Published height of an ?embed=1 iframe — the copy-paste snippet below, and the
// same number in /oembed and llms.txt (api/discover.R). MEASURED, not derived:
// the content is 664 px at 580 px wide and up, and the old guess (CHART_H + 130
// = 580) left every copied snippet with an inner scrollbar. A little headroom on
// top of 664, because a host whose font stack differs lays out slightly taller,
// and too-tall is invisible while too-short scrolls. Narrower frames need more
// (744 at 420 px, 784 at 330 px) — no single number can serve those, which is
// what lib/embed-height.ts is for.
const EMBED_HEIGHT = 680;

// What a zoom on the canvas does. A drag-zoom is a window of the user's own: no
// preset button is lit any more, and the next redraw keeps this window rather
// than a preset's. #7: when indexing to the viewport, a zoom must re-base to the
// new visible start, so drawChart re-runs (guarded against the setScale calls
// drawChart itself makes, so it can't loop); otherwise the window goes into the
// URL (r=).
let reindexing = false;
onChartEvents({
  onUserZoom: () => {
    if (state.range !== "custom") {
      state.range = "custom";
      $$("#range-seg .seg-btn").forEach(x => x.classList.remove("on"));
    }
  },
  onXScale: () => {
    if (!state.lastSeries) return;
    if (state.transform === "index" && !reindexing) {
      reindexing = true;
      requestAnimationFrame(() => { drawChart().finally(() => { reindexing = false; }); });
      return;   // drawChart() syncUrl()s at its end
    }
    if (!reindexing) syncUrl();
  },
});
async function drawChart() {
  drawsInFlight++;
  try { await drawChartNow(); } finally { drawsInFlight--; }
}
async function drawChartNow() {
  const el = $("#chart"); if (!el) return;
  const live = nav.draw();
  const stale = () => !live();
  // First paint (no plot yet): show a loading hint so a slow fetch reads as
  // "loading", not a broken/empty chart. A live plot (transform toggle) is left
  // untouched to avoid flicker / losing the current zoom.
  if (!hasChart()) el.innerHTML = `<div class="empty">${t("Loading…")}</div>`;
  // base lines: the current dataset's selection (typed + validated via fetchSeries)
  let base;
  try {
    const dims = {};
    for (const d of (state.current.meta.dim_order || []))
      if (state.selection[d] && state.selection[d].length) dims[d] = state.selection[d];
    const q = { dataset: state.current.id, dims };
    // One retry on a hard failure: a single dropped /series over a flaky mobile
    // pipe used to leave the chart blank with no recovery.
    try { base = await fetchSeries(q); }
    catch (e1) { if (stale()) return; base = await fetchSeries(q); }
    if (stale()) return;   // a newer drawChart superseded us — don't touch the chart
  } catch (e) { if (!stale()) el.innerHTML = `<div class="empty">Could not load series.</div>`; return; }
  // overlays: each extra is a single series from another dataset
  const overlays = [];
  for (const ex of state.extras) {
    try {
      const cm = metaCache[ex.datasetId] || await ensureMeta(ex.datasetId);
      let dp = "";
      if (ex.dim && ex.code) {
        const sel = Object.assign({}, (cm && cm.default) || {}); sel[ex.dim] = ex.code;
        dp = Object.entries(sel).map(([k, v]) => `${k}=${v}`).join(";");
      }
      const frame = await jget(`/series?dataset=${ex.datasetId}` + (dp ? `&dims=${encodeURIComponent(dp)}` : ""));
      if (stale()) return;
      const osel: Record<string, string[]> = {};
      for (const [k, v] of Object.entries((cm && cm.default) || {})) osel[k] = [String(v)];
      if (ex.dim && ex.code) osel[ex.dim] = [ex.code];
      overlays.push({ label: ex.label, sub: ex.isSub ? ex.datasetTitle : null, dim: ex.dim, code: ex.code,
        frame, freq: cm && cm.frequency, diff: cm ? changeDiff(cm, osel) : undefined, ref: ex });
    } catch (e) { /* skip a failed overlay */ }
  }
  if (stale()) return;   // overlay fetches awaited above — re-check before mutating
  // Lines, colours, labels and values (lib/chartmodel). The index view rebases
  // on the visible window, read here from the live plot, a pending URL window or
  // the range preset.
  const model = buildChartModel({
    meta: state.current.meta, selection: state.selection, base, overlays,
    transform: state.transform, title: chartTitle(), diff: baseDiff(),
    window: {
      scaleMin: chartScale()?.min ?? null,
      pendingMin: state.pendingRange ? state.pendingRange.min : null,
      range: state.range || "all",
    },
  });
  if (!model) {
    // No data for this combination. Tear down the chart fully so a later valid
    // selection recreates it (otherwise the stale plot lingers and never redraws).
    clearChart();
    state.lastSeries = null;
    // If this is the auto-default (stale/invalid stored default), recover by
    // adopting a real combination instead of showing an empty chart.
    if (state.autoDefault && !state.fellBack) { state.fellBack = true; el.innerHTML = ""; fallbackSelect(); return; }
    el.innerHTML = `<div class="empty">No data for this selection.</div>`;
    return;
  }
  // A relative view (% change, index) is undefined for values at or below zero
  // (lib/transforms): if every line came out empty, say so instead of an empty plot.
  if (state.transform !== "level" && model.lines.every(l => l.values.every(v => v == null))) {
    clearChart();
    state.lastSeries = null;
    syncTransformButtons();
    $("#unit-lbl").innerHTML = statLineHTML([], []);
    if (!state.embed) document.title = `${chartTitle()} · dataseries.org`;
    el.innerHTML = `<div class="empty">${esc(t("Not defined for values of zero or below. Use the Level view."))}</div>`;
    return;
  }
  const { dates } = model;
  const series: any[] = model.lines;
  const prevSeries = state.lastSeries;   // the lines the current window was drawn on
  state.lastSeries = { dates, series };
  state.pctMode = pctMode();   // ratio-of-GDP levels render as % (axis ×100)
  // uPlot and the visible window (chart.ts): a range preset or drag-zoom is
  // kept across the redraw, a URL window (#8) applies once. A dataset switch
  // cleared lastSeries (openDataset), so no zoom carries over.
  drawLines(el, model.x, { dates, series, freq: state.current.meta.frequency, pct: state.pctMode }, model.shift,
    { prevDates: prevSeries ? prevSeries.dates : null, range: state.range, pending: state.pendingRange });
  state.pendingRange = null;

  $("#unit-lbl").innerHTML = statLineHTML(dates, series);
  syncTransformButtons();
  // The title names the selection, so it follows it (unless being edited).
  const tEl = $("#sw-title");
  if (tEl && document.activeElement !== tEl && tEl.textContent !== chartTitle()) tEl.textContent = chartTitle();
  if (!state.embed) document.title = `${chartTitle()} · dataseries.org`;
  const lg = $("#legend");
  const nBase = model.nBase;
  const warn = nBase > LINE_WARN ? `<span class="leg-warn">${nBase} lines. Consider narrowing the selection for a readable chart.</span>` : "";
  // Selected combinations with no data draw nothing; say which, or a picked
  // series just fails to appear.
  const missLabels = model.missing;
  const miss = missLabels.length ? `<span class="leg-miss">${esc(t("No data for"))}: ${esc(missLabels.slice(0, 4).join(", "))}${missLabels.length > 4 ? ` ${esc(tn("and {n} more", missLabels.length - 4))}` : ""}</span>` : "";
  lg.innerHTML = warn + series.map(s =>
    `<span class="leg">${legSwatch(s.color, s.dash)}${esc(s.label)}${s.sub ? `<span class="leg-sub">· ${esc(s.sub)}</span>` : ""}${s.extraRef ? `<button class="leg-x" data-rm="${esc(extraKey(s.extraRef))}">${CLOSE}</button>` : ""}</span>`).join("") + miss;
  $$("[data-rm]", lg).forEach(b => b.onclick = () => {
    const e = state.extras.find(x => extraKey(x) === b.dataset.rm); if (e) removeExtra(e);
  });
  syncExtrasChip();
  // keep the Access snippet(s) in sync with what's plotted (multi-series, overlays)
  refreshAccess();
  syncUrl();   // live-reflect the current chart in the address bar (shareable)
}

// Trigger a browser download for a Blob.
function saveBlob(blob, name) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}
// File name for a download: the dataset id, plus what was picked or the user's
// own title, so a folder of rent and food charts is not five ch_fso_cpi.csv.
function fileStem() {
  const id = state.current.id, base = label(state.current.meta.title), title = chartTitle();
  const extra = title === base ? "" : title.startsWith(`${base}: `) ? title.slice(base.length + 2) : title;
  const slug = extra.normalize("NFD").replace(/\p{M}+/gu, "").toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60).replace(/-+$/, "");
  return slug ? `${id}_${slug}` : id;
}
function download(format) {
  const ls = state.lastSeries;
  if (!ls) return;
  const id = state.current.id, stem = fileStem() + (state.transform !== "level" ? `_${state.transform}` : "");

  // Image formats come from the SERVER renderer — the same recipe as the OG card
  // and inline chart — so there's one chart-drawing code path, not a separate
  // client export. Carry the whole view (dims, transform, range, overlays) via the
  // shareHash query so the downloaded file matches exactly; drop the title param.
  if (format === "png" || format === "svg") {
    const h = shareHash(), qi = h.indexOf("?");
    const sp = new URLSearchParams(qi >= 0 ? h.slice(qi + 1) : "");
    sp.delete("title"); sp.set("dataset", id);
    const a = document.createElement("a");
    a.href = `/chart.${format}?${sp.toString()}`;
    a.download = `${stem}.${format}`;
    document.body.appendChild(a); a.click(); a.remove();
    return;
  }

  // shared rectangular shape: header + one row per date. A computed view says
  // so in each column name and the file name, and its values are rounded to
  // six decimals (the division leaves 15 digits nobody published).
  const computed = state.transform !== "level";
  const header = ["date", ...ls.series.map(s => computed ? `${s.label} (${transformUnit()})` : s.label)];
  const round = (v: number) => computed ? Math.round(v * 1e6) / 1e6 : v;
  const rows = ls.dates.map((t, i) => [t, ...ls.series.map(s => s.values[i] == null ? null : round(s.values[i] as number))]);

  if (format === "json") {
    const obj = { provider: "dataseries.org", dataset: id, title: chartTitle(), unit: transformUnit(), transform: state.transform,
      series: ls.series.map(s => ({ name: s.label, observations: ls.dates.map((t, i) => ({ date: t, value: s.values[i] })).filter(o => o.value != null) })) };
    saveBlob(new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" }), stem + ".json");
  } else if (format === "xls" || format === "xlsx") {
    // REAL .xlsx via SheetJS — numbers stay numeric, nulls become blank cells.
    const aoa = [header, ...rows.map(r => r.map(c => c == null ? "" : c))];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "data");
    XLSX.writeFile(wb, stem + ".xlsx");
  } else {
    // CSV: UTF-8 BOM so Excel reads accented/en-dash labels; every field quoted.
    saveBlob(new Blob([CSV_BOM + toCsv(header, rows) + "\r\n"], { type: "text/csv;charset=utf-8" }), stem + ".csv");
  }
}


// ---- boot -------------------------------------------------------------------
async function init() {
  try {
    initLocale();                       // ?lang= / stored / browser -> label(), t()
    const route = getRoute();           // path /d/<id> OR legacy #/d/<id>
    const deep = !!route;
    // Theme: the site follows the visitor's stored preference, an embed follows
    // the page it is embedded in (initEmbedTheme, from restoreFromUrl).
    if (!(route && route.embed)) initSiteTheme();
    if (deep) state.view = "loading";   // never flash the landing before a shared chart
    // The maps list is extra: requested alongside the catalog, but the page never
    // waits for it. It fills the map icons and rows in once it lands (onMapsLoaded).
    const mapsP = fetchMaps().catch(() => []);
    state.catalog = await fetchCatalog() as CatalogItem[];   // zod types are all-optional outside strict mode
    // Category order is data-driven (data/categories.json). Non-fatal: on failure
    // the overview falls back to the built-in CONCEPT_ORDER constant.
    state.categories = await fetchCategories().catch(() => []);
    const q0 = !deep && new URLSearchParams(location.search).get("q");
    if (q0) { state.query = q0; state.view = "results"; }
    // /datasets/maps opens the browse view on its Maps tab. That tab is the
    // maps list, so here the first paint does wait for it.
    if (!deep && placeFromUrl(location.pathname, location.search, location.hash, null).tab === "maps") {
      state.view = "results"; state.tab = "maps";
      state.maps = (await mapsP) || [];
    }
    render();
    mapsP.then(maps => { state.maps = maps || []; onMapsLoaded(); });
    if (deep) {
      const ok = await restoreFromUrl();  // flips to detail + draws the shared chart
      if (!ok) { state.view = "landing"; renderView(); }  // unknown id -> fall back
    }
    // Load the sub-series search index in the background, deferred to idle so it
    // never competes with a click's chart fetch (that race left the chart blank).
    // One ~22 KB request now, not ~53 metas — and only needed once you search.
    const idle = window.requestIdleCallback || (cb => setTimeout(cb, 200));
    idle(() => buildSubIndex().catch(() => {}));
  } catch (e) {
    app().innerHTML = `<div class="empty">Could not load catalog: ${esc(e.message)}</div>`;
  }
}
// The maps arrived after the first paint: redraw what shows them, including a
// dropdown someone is already typing into.
function onMapsLoaded() {
  if (!state.maps.length) return;
  if (state.view === "results") {
    // The Maps tab's boot waited for the list, so its tabs are drawn already.
    if (!$(".browse-tabs")) { $("#results-body").innerHTML = resultsBodyHTML(); wireBody(); }
    syncNav();
  }
  else if (state.view === "landing" && !$(".chip-map")) $(".hero-eg-chips")?.insertAdjacentHTML("beforeend", mapChipHTML());
  else if (state.view === "detail" && !$("#map-pill") && $("#title-share"))
    $("#title-share").insertAdjacentHTML("afterend", mapPillHTML());
  refreshOpenDropdowns();
}
// Re-run the query of a search dropdown someone has open, when what it searches
// has grown (the maps list, the sub-series index). Otherwise it keeps the
// results from before, until the next keystroke.
function refreshOpenDropdowns() {
  for (const [q, d] of [["#hero-q", "#hero-drop"], ["#detail-q", "#detail-drop"]])
    if ($(d) && !$(d).classList.contains("hidden")) $(q).dispatchEvent(new Event("input"));
}
// Back/forward through shared detail links — and WhatsApp's in-app browser, which
// reuses the document and re-navigates (a hashchange) instead of reloading.
window.addEventListener("hashchange", () => {
  if (location.hash.startsWith("#/d/")) restoreFromUrl();
});
// Back/forward (History API): show whatever view the entry stands for — a
// dataset, the results for a text, the browse view or the landing.
window.addEventListener("popstate", e => {
  const p = placeFromUrl(location.pathname, location.search, location.hash, e.state);
  if (p.view === "detail") { restoreFromUrl(); return; }
  nav.go();   // a dataset still loading must not pull the view back to itself
  state.query = p.query;
  state.view = p.view;
  state.tab = p.tab || "datasets";
  renderView();
});
init();
